<template>
  <div class="w-full relative lg:mt-0 -mt-4 bg-gray-200">
    <div v-if="showPublishAll"
      class="w-full z-50 flex items-center justify-center h-screen fixed top-0 left-0 bg-secondary bg-opacity-50">
      <div class="w-96 py-20 px-10 rounded-lg p-4 bg-white shadow-lg">
        You are about to publish all jobs at once, are you sure you want to do
        this?
        <div class="w-full flex justify-center space-x-2 mt-10">
          <div class="flex-shrink">
            <div @click="showPublishAll = false"
              class="h-14 bg-secondary px-4 cursor-pointer hover:bg-gray-900 text-white rounded flex items-center justify-center">
              No, Go Back
            </div>
          </div>
          <div class="flex-shrink">
            <div @click="publishAll()"
              class="h-14 bg-primary px-4 cursor-pointer hover:bg-gray-900 text-white rounded flex items-center justify-center">
              Yes Publish All
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="signOff >= 0" class="w-full z-50 h-screen fixed top-0 left-0 bg-secondary bg-opacity-50">
      <div class="w-full h-full relative flex items-center justify-center">
        <div class="w-96 relative py-20 px-10 rounded-lg p-4 bg-white shadow-lg">
          <div class="absolute top-0 right-0 m-2">
            <i @click="signOff = -1" class="fa-solid fa-xmark cursor-pointer text-secondary hover:text-primary"></i>
          </div>
          Please select if you would like to mark the job as completed or
          cancelled.
          <div class="w-full flex justify-center space-x-2 mt-10">
            <div class="flex-shrink">
              <div @click="cancelJob()"
                class="h-14 bg-secondary px-4 cursor-pointer hover:bg-gray-900 text-white rounded flex items-center justify-center">
                Cancel Job
              </div>
            </div>
            <div class="flex-shrink">
              <div @click="completeJob()"
                class="h-14 bg-primary px-4 cursor-pointer hover:bg-gray-900 text-white rounded flex items-center justify-center">
                Complete Job
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- En-Route Confirmation Modal -->
    <div v-if="enRouteModal && enRouteJob" class="w-full z-50 h-screen fixed top-0 left-0 bg-secondary bg-opacity-50" style="backdrop-filter:blur(2px)">
      <div class="w-full h-full relative flex items-center justify-center">
        <div class="w-96 relative py-10 px-8 rounded-xl bg-white shadow-2xl">
          <div class="absolute top-0 right-0 m-3">
            <i @click="enRouteModal = false; enRouteJob = null" class="fa-solid fa-xmark cursor-pointer text-gray-400 hover:text-gray-600"></i>
          </div>
          <div class="text-center mb-6">
            <div class="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <i class="fa-solid fa-route text-yellow-600 text-xl"></i>
            </div>
            <h3 class="text-lg font-bold text-gray-900">Send En-Route Notification</h3>
          </div>
          <p class="text-sm text-gray-600 text-center mb-2">
            Send en-route notification to <span class="font-semibold">{{ enRouteJob.jobContact || enRouteJob.contact || enRouteJob.company }}</span>?
          </p>
          <p class="text-xs text-gray-400 text-center mb-6">
            An SMS will be sent to <span class="font-medium">{{ enRouteJob.jobPhone || enRouteJob.phone }}</span> letting them know you're on your way.
          </p>
          <div class="flex justify-center space-x-3">
            <button @click="enRouteModal = false; enRouteJob = null"
              class="px-4 h-10 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
              Cancel
            </button>
            <button @click="sendEnRoute()" :disabled="enRouteSending"
              class="px-4 h-10 text-sm font-medium text-white bg-yellow-500 rounded-lg hover:bg-yellow-600 transition-colors disabled:opacity-50">
              <i v-if="enRouteSending" class="fa-solid fa-spinner fa-spin mr-1.5"></i>
              {{ enRouteSending ? 'Sending...' : 'Send & Set En-Route' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Next Job Modal -->
    <div v-if="nextJobModal" class="w-full z-50 h-screen fixed top-0 left-0 bg-secondary bg-opacity-50" style="backdrop-filter:blur(2px)">
      <div class="w-full h-full relative flex items-center justify-center">
        <div class="w-96 relative py-10 px-8 rounded-xl bg-white shadow-2xl">
          <div class="absolute top-0 right-0 m-3">
            <i @click="nextJobModal = false; nextJob = null" class="fa-solid fa-xmark cursor-pointer text-gray-400 hover:text-gray-600"></i>
          </div>
          <!-- Has next job -->
          <template v-if="nextJob">
            <div class="text-center mb-6">
              <div class="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <i class="fa-solid fa-arrow-right text-green-600 text-xl"></i>
              </div>
              <h3 class="text-lg font-bold text-gray-900">Next Job</h3>
            </div>
            <div class="bg-gray-50 rounded-lg p-4 mb-6">
              <p class="text-sm text-gray-900 font-semibold">{{ nextJob.company }}</p>
              <p class="text-sm text-gray-600 mt-1">
                <i class="fa-solid fa-clock text-gray-400 mr-1"></i>
                {{ $moment(nextJob.time).format('h:mm A') }} — {{ getMinutesFromNow(nextJob.time) }} minutes from now
              </p>
              <p v-if="nextJob.fullAddress" class="text-xs text-gray-500 mt-1">
                <i class="fa-solid fa-location-dot text-gray-400 mr-1"></i>
                {{ nextJob.fullAddress }}
              </p>
            </div>
            <div class="flex justify-center space-x-3">
              <button @click="nextJobModal = false; nextJob = null"
                class="px-4 h-10 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
                Later
              </button>
              <button v-if="!nextJob.enRoute" @click="enRouteJob = nextJob; nextJobModal = false; nextJob = null; enRouteModal = true"
                class="px-4 h-10 text-sm font-medium text-white bg-yellow-500 rounded-lg hover:bg-yellow-600 transition-colors">
                <i class="fa-solid fa-route mr-1.5"></i>Set En-Route Now
              </button>
            </div>
          </template>
          <!-- No more jobs -->
          <template v-else>
            <div class="text-center">
              <div class="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <i class="fa-solid fa-check text-green-600 text-xl"></i>
              </div>
              <h3 class="text-lg font-bold text-gray-900 mb-2">All Jobs Completed</h3>
              <p class="text-sm text-gray-500 mb-6">You have no more jobs scheduled for today.</p>
              <button @click="nextJobModal = false"
                class="px-5 h-10 text-sm font-medium text-white bg-green-500 rounded-lg hover:bg-green-600 transition-colors">
                Close
              </button>
            </div>
          </template>
        </div>
      </div>
    </div>

    <div v-if="showTesters" class="w-full z-40 h-screen fixed top-0 left-0 bg-secondary bg-opacity-50">
      <div class="w-full h-full relative flex items-center justify-center">
        <div class="w-96 h-96 overflow-y-scroll relative rounded-lg bg-white shadow-lg">
          <div class="h-20 w-full bg-gray-900 text-left px-6 text-white rounded-t-lg flex items-center">
            <div class="flex-grow">
              <h3 class="text-white text-lg">Select a tester</h3>
            </div>
            <div class="flex-shrink ml-auto">
              <i @click="showTesters = false"
                class="fa-solid fa-xmark cursor-pointer text-white hover:text-primary"></i>
            </div>
          </div>

          <!-- {{ smsTester.testerPhone }} -->
          <div class="w-full flex px-6 flex-wrap justify-center space-x-2 mt-4">
            <div @click="removeTester(selectedJob)"
              class="mb-4 w-full h-10 flex items-center justify-center cursor-pointer text-red-600 border border-red-600 bg-red-100 hover:bg-red-800 hover:text-white rounded-lg">
              Remove Tester
            </div>
            <div class="w-full" v-for="(t, tindex) in testers" :key="'tester' + tindex">

              <div @click="updateTester(t, tindex, selectedJob)"
                class="w-full rounded-lg px-4 hover:bg-gray-100 h-10 cursor-pointer flex items-center text-sm text-gray-600">
                {{ t.name }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showNotificationPopup.length > 0"
      class="w-full z-40 h-screen fixed top-0 left-0 bg-secondary bg-opacity-50">
      <div class="w-full h-full relative flex items-center justify-center">
        <div class="w-96 h-96 overflow-y-scroll relative rounded-lg bg-white shadow-lg">
          <div class="h-20 w-full bg-gray-900 text-left px-6 text-white rounded-t-lg flex items-center">
            <div class="flex-grow">
              <h3 class="text-white text-lg">Notify testers</h3>
            </div>
            <div class="flex-shrink ml-auto">
              <i @click="showNotificationPopup = []"
                class="fa-solid fa-xmark cursor-pointer text-white hover:text-primary"></i>
            </div>
          </div>

          <!-- {{ smsTester.testerPhone }} -->
          <div class="w-full flex px-6 flex-wrap justify-center space-x-2 mt-4">
            <h2 class="text-sm mb-4">
              Would you like to notify the testers of this change?
            </h2>
            <div class="w-full h-10 rounded hover:bg-gray-200 bg-gray-100 mb-2 px-4 flex items-center text-left"
              v-for="(t, index) in showNotificationPopup" :key="index">
              <label :for="t.name" class="w-full cursor-pointer">
                <div class="w-full flex">
                  <div class="flex-shrink mr-2">
                    <input type="checkbox" v-model="t.selected" :name="t.name" :id="t.name" />
                  </div>
                  <div class="flex-grow">{{ t.name }}</div>
                  <div class="flex-shrink">
                    <span v-if="!t.winningJob" class="rounded-full text-xs bg-secondary text-white px-3 py-1">Removing
                      Job</span>
                    <span v-if="t.winningJob" class="rounded-full text-xs bg-secondary text-white px-3 py-1">Adding
                      Job</span>
                  </div>
                </div>
              </label>
            </div>

            <div @click="sendChangeNotification()"
              class="w-full h-12 mt-4 bg-primary hover:bg-secondary text-white rounded cursor-pointer flex items-center justify-center">
              Send Notifications
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="smsModal" class="w-full z-50 h-screen fixed top-0 left-0 bg-secondary bg-opacity-50">
      <div class="w-full h-full relative flex items-center justify-center">
        <div class="w-96 relative py-20 px-10 rounded-lg p-4 bg-white shadow-lg">
          <div class="absolute top-0 right-0 m-2">
            <i @click="(showTesters = false), (smsModal = false)"
              class="fa-solid fa-xmark cursor-pointer text-secondary hover:text-primary"></i>
          </div>
          Write a message to send
          <!-- {{ smsTester.testerPhone }} -->
          <div v-if="visibleSmsTemplates.length > 0" class="mt-3">
            <select v-model="selectedSmsTemplateId" @change="applySmsTemplate('smsBox')"
              class="w-full h-10 rounded bg-gray-100 px-3 text-sm text-gray-600 outline-none border border-gray-200 cursor-pointer">
              <option value="">Custom message</option>
              <option v-for="t in visibleSmsTemplates" :key="t.id" :value="t.id">{{ t.name }}</option>
            </select>
          </div>
          <div class="w-full flex flex-wrap justify-center space-x-2 mt-4">
            <textarea class="w-full h-32 rounded bg-gray-100 p-4 text-gray-500 text-sm my-2" v-model="smsBox" cols="30"
              rows="10"></textarea>
            <div class="flex-grow">
              <div @click="sendSms()"
                class="h-14 bg-primary px-4 w-full cursor-pointer hover:bg-gray-900 text-white rounded flex items-center justify-center">
                Send SMS
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Client SMS Modal -->
    <div v-if="clientSmsModal" class="w-full z-50 h-screen fixed top-0 left-0 bg-secondary bg-opacity-50 flex items-center justify-center" style="backdrop-filter:blur(2px)">
      <div class="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 flex flex-col" style="max-height:90vh">
        <!-- Header -->
        <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div>
            <h3 class="text-lg font-bold text-gray-900">
              <i class="fa-solid fa-comment-sms text-indigo-600 mr-2"></i>Send Client SMS
            </h3>
            <p v-if="clientSmsJob" class="text-xs text-gray-400 mt-0.5">{{ clientSmsJob.company }} — {{ clientSmsJob.projectName || clientSmsJob.fullAddress || '' }}</p>
          </div>
          <button @click="clientSmsModal = false" class="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <!-- Job details card -->
        <div v-if="clientSmsJob" class="px-6 pt-5 flex-shrink-0">
          <div class="bg-indigo-50 border border-indigo-100 rounded-lg px-4 py-3">
            <div class="flex items-center gap-3">
              <div class="flex items-center gap-1.5 min-w-0">
                <label class="text-xs text-indigo-400 flex-shrink-0 font-medium">Status</label>
                <select v-model="clientSmsJob.status" @change="clientSmsJobDirty = true; clientSmsJobSaved = false"
                  class="text-xs font-medium rounded-md px-2 py-1.5 outline-none border cursor-pointer bg-white"
                  :class="{
                    'text-yellow-700 border-yellow-200': clientSmsJob.status === 'ON-CALL',
                    'text-green-700 border-green-200': clientSmsJob.status === 'FIRM',
                    'text-blue-700 border-blue-200': clientSmsJob.status === 'COMMITTED',
                    'text-red-700 border-red-200': clientSmsJob.status === 'CANCELLED',
                    'text-gray-700 border-gray-200': clientSmsJob.status === 'COMPLETED',
                  }">
                  <option>ON-CALL</option>
                  <option>FIRM</option>
                  <option>COMMITTED</option>
                  <option>CANCELLED</option>
                  <option>COMPLETED</option>
                </select>
              </div>
              <div class="flex items-center gap-1.5 flex-1 min-w-0">
                <label class="text-xs text-indigo-400 flex-shrink-0 font-medium">Date &amp; Time</label>
                <flat-pickr v-model="clientSmsJob.date" :config="clientSmsDateConfig" @on-change="clientSmsJobDirty = true; clientSmsJobSaved = false"
                  class="text-xs border border-indigo-200 rounded-md px-2 py-1.5 outline-none w-full bg-white" />
              </div>
              <div class="flex items-center gap-1.5 min-w-0" style="width:90px">
                <label class="text-xs text-indigo-400 flex-shrink-0 font-medium">m³</label>
                <input type="text" v-model="clientSmsJob.meters" @input="clientSmsJobDirty = true; clientSmsJobSaved = false"
                  placeholder="0" class="text-xs border border-indigo-200 rounded-md px-2 py-1.5 outline-none w-full bg-white" />
              </div>
              <button v-if="clientSmsJobDirty && !clientSmsJobSaved" @click="saveClientSmsJob()" :disabled="clientSmsJobSaving"
                class="flex-shrink-0 text-xs font-medium bg-indigo-600 text-white px-3 py-1.5 rounded-md hover:bg-indigo-700 disabled:opacity-50">
                <i v-if="clientSmsJobSaving" class="fa-solid fa-spinner fa-spin mr-1"></i>
                Save Job
              </button>
              <span v-if="clientSmsJobSaved" class="flex-shrink-0 text-xs text-green-600 font-semibold bg-green-50 border border-green-200 rounded-md px-3 py-1.5">
                <i class="fa-solid fa-check mr-1"></i>Job Saved
              </span>
            </div>
          </div>
        </div>

        <!-- Body -->
        <div class="flex-1 overflow-y-auto px-6 py-5" style="min-height:0">
          <!-- Send To -->
          <div v-if="clientSmsJob" class="mb-5">
            <label class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 block">Send To</label>
            <div class="space-y-2">
              <!-- Primary contact — clickable card -->
              <div @click="clientSmsRecipient = clientSmsRecipient === 'both' ? 'secondary' : 'primary'"
                class="border rounded-lg p-3 cursor-pointer transition-all flex items-center gap-3"
                :class="clientSmsRecipient === 'primary' || clientSmsRecipient === 'both' ? 'border-indigo-400 bg-indigo-50 ring-1 ring-indigo-200' : 'border-gray-200 bg-white hover:border-gray-300'">
                <div class="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                  :class="clientSmsRecipient === 'primary' || clientSmsRecipient === 'both' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-400'">
                  <i class="fa-solid fa-user text-xs"></i>
                </div>
                <div class="flex-1 min-w-0 text-left">
                  <div class="flex items-center gap-2">
                    <span class="text-sm font-medium text-gray-800">{{ clientSmsJob.jobContact || clientSmsJob.contact || '—' }}</span>
                    <span class="text-xs text-gray-400 font-normal">Primary</span>
                  </div>
                  <div class="flex items-center gap-3 mt-0.5">
                    <span class="text-xs text-gray-500"><i class="fa-solid fa-phone mr-1" style="font-size:9px"></i>{{ clientSmsJob.jobPhone || clientSmsJob.phone || 'No phone' }}</span>
                    <span v-if="clientSmsJob.email" class="text-xs text-gray-400"><i class="fa-solid fa-envelope mr-1" style="font-size:9px"></i>{{ clientSmsJob.email }}</span>
                  </div>
                </div>
                <div class="flex-shrink-0">
                  <div class="w-5 h-5 rounded border-2 flex items-center justify-center transition-colors"
                    :class="clientSmsRecipient === 'primary' || clientSmsRecipient === 'both' ? 'border-indigo-600 bg-indigo-600' : 'border-gray-300 bg-white'">
                    <i v-if="clientSmsRecipient === 'primary' || clientSmsRecipient === 'both'" class="fa-solid fa-check text-white" style="font-size:10px"></i>
                  </div>
                </div>
              </div>

              <!-- Secondary contact — clickable card -->
              <div @click="clientSmsJob.secondaryPhone ? (clientSmsRecipient = clientSmsRecipient === 'both' ? 'primary' : 'secondary') : null"
                class="border rounded-lg p-3 transition-all flex items-center gap-3"
                :class="[
                  clientSmsRecipient === 'secondary' || clientSmsRecipient === 'both' ? 'border-indigo-400 bg-indigo-50 ring-1 ring-indigo-200' : 'border-gray-200 bg-white',
                  clientSmsJob.secondaryPhone ? 'cursor-pointer hover:border-gray-300' : 'opacity-40 cursor-not-allowed'
                ]">
                <div class="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                  :class="clientSmsRecipient === 'secondary' || clientSmsRecipient === 'both' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-400'">
                  <i class="fa-solid fa-user text-xs"></i>
                </div>
                <div class="flex-1 min-w-0 text-left">
                  <div class="flex items-center gap-2">
                    <span class="text-sm font-medium text-gray-800">{{ clientSmsJob.secondaryContact || 'Not set' }}</span>
                    <span class="text-xs text-gray-400 font-normal">Secondary</span>
                  </div>
                  <div class="flex items-center gap-3 mt-0.5">
                    <span class="text-xs text-gray-500"><i class="fa-solid fa-phone mr-1" style="font-size:9px"></i>{{ clientSmsJob.secondaryPhone || 'No phone' }}</span>
                    <span v-if="clientSmsJob.secondaryEmail" class="text-xs text-gray-400"><i class="fa-solid fa-envelope mr-1" style="font-size:9px"></i>{{ clientSmsJob.secondaryEmail }}</span>
                  </div>
                </div>
                <div class="flex-shrink-0">
                  <div class="w-5 h-5 rounded border-2 flex items-center justify-center transition-colors"
                    :class="clientSmsRecipient === 'secondary' || clientSmsRecipient === 'both' ? 'border-indigo-600 bg-indigo-600' : 'border-gray-300 bg-white'">
                    <i v-if="clientSmsRecipient === 'secondary' || clientSmsRecipient === 'both'" class="fa-solid fa-check text-white" style="font-size:10px"></i>
                  </div>
                </div>
              </div>

              <!-- Both + Manual row -->
              <div class="flex items-center gap-2 pt-1">
                <button @click="clientSmsRecipient = 'both'"
                  :disabled="!(clientSmsJob.jobPhone || clientSmsJob.phone) || !clientSmsJob.secondaryPhone"
                  class="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border disabled:opacity-30 disabled:cursor-not-allowed"
                  :class="clientSmsRecipient === 'both' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'">
                  <i class="fa-solid fa-users mr-1.5"></i>Select Both
                </button>
                <button @click="clientSmsRecipient = 'manual'"
                  class="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border"
                  :class="clientSmsRecipient === 'manual' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'">
                  <i class="fa-solid fa-pen mr-1.5"></i>Enter Manually
                </button>
              </div>
            </div>
          </div>

          <!-- Manual phone input -->
          <div v-if="clientSmsRecipient === 'manual'" class="mb-5">
            <label class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 block">Phone Number</label>
            <input type="text" v-model="clientSmsManualPhone" placeholder="+614XXXXXXXX"
              class="w-full h-10 rounded-lg border border-gray-200 outline-none px-3 text-sm text-gray-700 focus:border-indigo-400 transition-colors" />
          </div>

          <!-- Template selector -->
          <div v-if="visibleSmsTemplates.length > 0" class="mb-5">
            <label class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 block text-left">Template</label>
            <select v-model="selectedSmsTemplateId" @change="applySmsTemplate('clientSmsMessage')"
              class="w-full h-10 rounded-lg border border-gray-200 px-3 text-sm text-gray-700 outline-none focus:border-indigo-400 bg-white cursor-pointer">
              <option value="">Custom message</option>
              <option v-for="t in visibleSmsTemplates" :key="t.id" :value="t.id">{{ t.name }}</option>
            </select>
          </div>

          <!-- Message compose -->
          <div class="mb-5">
            <label class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 block">Message</label>
            <textarea v-model="clientSmsMessage" rows="5" ref="clientSmsTextarea"
              class="w-full rounded-lg border border-gray-200 p-3 text-sm text-gray-700 outline-none focus:border-indigo-400 transition-colors resize-none"></textarea>
            <div class="mt-2">
              <p class="text-xs text-gray-400 mb-1.5 text-left">Shortcodes — click to insert:</p>
              <div class="flex flex-wrap gap-1.5">
                <button v-for="sc in clientSmsShortcodes" :key="sc.code" @click="insertClientSmsShortcode(sc.code)"
                  class="inline-flex items-center px-2.5 py-1 rounded-md bg-indigo-50 text-indigo-700 text-xs font-medium hover:bg-indigo-100 transition-colors cursor-pointer">
                  <code class="mr-1">{{ sc.code }}</code>
                  <span class="text-indigo-400 font-normal">{{ sc.label }}</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Message history -->
          <div v-if="clientSmsHistory.length > 0">
            <label class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 block">Message History</label>
            <div class="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-48 overflow-y-auto">
              <div v-for="(msg, idx) in clientSmsHistory" :key="idx"
                class="px-4 py-3 text-sm">
                <div class="flex items-center justify-between mb-1">
                  <div class="flex items-center gap-2">
                    <i :class="msg.direction === 'inbound' ? 'fa-solid fa-arrow-down text-blue-500' : 'fa-solid fa-arrow-up text-gray-400'" class="text-xs"></i>
                    <span class="text-xs font-semibold" :class="msg.direction === 'inbound' ? 'text-blue-600' : 'text-gray-600'">
                      {{ msg.direction === 'inbound' ? 'Received' : 'Sent' }}
                    </span>
                  </div>
                  <div class="flex items-center gap-2">
                    <span v-if="msg.classification" class="text-xs px-2 py-0.5 rounded-full font-medium"
                      :class="{
                        'bg-green-100 text-green-700': msg.classification === 'FIRM',
                        'bg-yellow-100 text-yellow-700': msg.classification === 'CHANGED',
                        'bg-red-100 text-red-700': msg.classification === 'CANCELLED',
                      }">{{ msg.classification }}</span>
                    <span v-if="msg.timestamp" class="text-xs text-gray-400">{{ formatSmsTimestamp(msg.timestamp) }}</span>
                  </div>
                </div>
                <p class="text-sm text-gray-700 text-left">{{ msg.body }}</p>
              </div>
            </div>
          </div>
          <div v-else-if="!clientSmsLoading" class="text-xs text-gray-400 text-center py-4 border border-dashed border-gray-200 rounded-lg">
            No message history for this job.
          </div>
        </div>

        <!-- Footer -->
        <div class="px-6 py-4 border-t border-gray-200 flex-shrink-0">
          <div class="flex items-center justify-between">
            <div class="text-xs text-gray-400">
              <i class="fa-solid fa-info-circle mr-1"></i>
              {{ clientSmsRecipient === 'both' ? 'Will send to both contacts' : clientSmsRecipient === 'manual' ? 'Sending to manual number' : 'Sending to ' + clientSmsRecipient + ' contact' }}
            </div>
            <div class="flex items-center gap-2">
              <button @click="clientSmsModal = false"
                class="px-4 h-10 rounded-lg text-sm font-medium bg-white text-gray-600 border border-gray-200 hover:bg-gray-100 transition-colors">
                Cancel
              </button>
              <button @click="sendClientSms()"
                :disabled="clientSmsLoading"
                class="px-5 h-10 rounded-lg text-sm font-medium inline-flex items-center bg-indigo-600 text-white hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                <i v-if="clientSmsLoading" class="fa-solid fa-spinner fa-spin mr-2"></i>
                <i v-else class="fa-solid fa-paper-plane mr-2" style="font-size:11px"></i>
                {{ clientSmsLoading ? 'Sending...' : 'Send SMS' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Client Link Modal -->
    <div v-if="linkModal" class="w-full z-50 h-screen fixed top-0 left-0 bg-secondary bg-opacity-50">
      <div class="w-full h-full flex items-center justify-center">
        <div class="bg-white rounded-xl shadow-xl p-6 w-full max-w-lg relative">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-lg font-bold text-gray-900">
              <i class="fa-solid fa-link text-green-600 mr-2"></i>Client Job Link
            </h3>
            <i @click="linkModal = false"
              class="fa-solid fa-xmark cursor-pointer text-secondary hover:text-primary"></i>
          </div>

          <div v-if="linkJob" class="mb-3 text-sm text-gray-500">
            <span class="font-medium text-gray-700">{{ linkJob.company }}</span>
            <span v-if="linkJob.formattedAddress"> — {{ linkJob.formattedAddress }}</span>
          </div>

          <div class="flex items-center mb-4">
            <input type="text" :value="linkUrl" readonly
              class="rounded-l h-10 bg-gray-100 flex-1 outline-none px-3 text-gray-600 text-sm border border-r-0 border-gray-200"
              @click="$event.target.select()" />
            <button @click="copyLink"
              class="h-10 px-4 rounded-r text-sm font-medium flex items-center"
              :class="linkCopied ? 'bg-green-600 text-white' : 'bg-indigo-600 text-white hover:bg-indigo-800'">
              <i :class="linkCopied ? 'fa-solid fa-check' : 'fa-regular fa-copy'" class="mr-1"></i>
              {{ linkCopied ? 'Copied!' : 'Copy' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Client SMS Alert Modal -->
    <div v-if="clientAlert" class="w-full z-50 h-screen fixed top-0 left-0 bg-secondary bg-opacity-50 flex items-center justify-center">
      <transition name="bounce">
        <div class="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md relative animate-bounce-in">
          <div class="flex items-center justify-center mb-4">
            <div class="w-14 h-14 rounded-full flex items-center justify-center"
              :class="{
                'bg-red-100': clientAlert.status === 'CANCELLED',
                'bg-yellow-100': clientAlert.status === 'CHANGED',
                'bg-green-100': clientAlert.status === 'FIRM',
              }">
              <i class="text-2xl"
                :class="{
                  'fa-solid fa-circle-xmark text-red-500': clientAlert.status === 'CANCELLED',
                  'fa-solid fa-circle-question text-yellow-500': clientAlert.status === 'CHANGED',
                  'fa-solid fa-circle-check text-green-500': clientAlert.status === 'FIRM',
                }"></i>
            </div>
          </div>
          <h3 class="text-lg font-bold text-center mb-1"
            :class="{
              'text-red-700': clientAlert.status === 'CANCELLED',
              'text-yellow-700': clientAlert.status === 'CHANGED',
              'text-green-700': clientAlert.status === 'FIRM',
            }">
            {{ clientAlert.status === 'CANCELLED' ? 'Job Cancelled' : clientAlert.status === 'CHANGED' ? 'Change Requested' : 'Job Confirmed' }}
          </h3>
          <p class="text-sm text-gray-500 text-center mb-4">Client responded via {{ clientAlert.message ? 'SMS' : 'portal link' }}</p>
          <div class="bg-gray-50 rounded-lg p-4 text-sm space-y-1 mb-4">
            <div class="flex"><span class="text-gray-500 w-20 flex-shrink-0">Company</span><span class="font-medium text-gray-800">{{ clientAlert.company }}</span></div>
            <div v-if="clientAlert.projectName" class="flex"><span class="text-gray-500 w-20 flex-shrink-0">Project</span><span class="font-medium text-gray-800">{{ clientAlert.projectName }}</span></div>
            <div v-if="clientAlert.date" class="flex"><span class="text-gray-500 w-20 flex-shrink-0">Date</span><span class="font-medium text-gray-800">{{ clientAlert.date }}</span></div>
            <div v-if="clientAlert.address" class="flex"><span class="text-gray-500 w-20 flex-shrink-0">Address</span><span class="font-medium text-gray-800">{{ clientAlert.address }}</span></div>
            <div v-if="clientAlert.message" class="flex"><span class="text-gray-500 w-20 flex-shrink-0">Message</span><span class="font-medium text-gray-800">{{ clientAlert.message }}</span></div>
          </div>
          <button @click="clientAlert = null"
            class="w-full h-12 rounded-lg bg-secondary text-white font-semibold hover:opacity-90">
            Dismiss
          </button>
        </div>
      </transition>
    </div>

    <!-- SMS Resolve Modal -->
    <div v-if="changeResolveModal" class="w-full z-50 h-screen fixed top-0 left-0 bg-secondary bg-opacity-50 flex items-center justify-center" style="backdrop-filter:blur(2px)">
      <div class="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6">
        <div v-if="changeResolveJob" class="flex items-center justify-center mb-4">
          <div class="w-14 h-14 rounded-full flex items-center justify-center"
               :class="{
                 'bg-green-100': changeResolveJob.clientSmsStatus === 'FIRM',
                 'bg-yellow-100': changeResolveJob.clientSmsStatus === 'CHANGED',
                 'bg-red-100': changeResolveJob.clientSmsStatus === 'CANCELLED',
               }">
            <i class="text-2xl"
               :class="{
                 'fa-solid fa-circle-check text-green-500': changeResolveJob.clientSmsStatus === 'FIRM',
                 'fa-solid fa-circle-question text-yellow-500': changeResolveJob.clientSmsStatus === 'CHANGED',
                 'fa-solid fa-circle-xmark text-red-500': changeResolveJob.clientSmsStatus === 'CANCELLED',
               }"></i>
          </div>
        </div>
        <h3 class="text-lg font-bold text-center mb-4"
            :class="{
              'text-green-700': changeResolveJob && changeResolveJob.clientSmsStatus === 'FIRM',
              'text-yellow-700': changeResolveJob && changeResolveJob.clientSmsStatus === 'CHANGED',
              'text-red-700': changeResolveJob && changeResolveJob.clientSmsStatus === 'CANCELLED',
            }">
          {{ changeResolveJob && changeResolveJob.clientSmsStatus === 'FIRM' ? 'Resolve Confirmation' : changeResolveJob && changeResolveJob.clientSmsStatus === 'CANCELLED' ? 'Resolve Cancellation' : 'Resolve Change Request' }}
        </h3>
        <div v-if="changeResolveJob && changeResolveJob.clientSmsMessage" class="border rounded-lg p-3 mb-4"
             :class="{
               'bg-green-50 border-green-200': changeResolveJob.clientSmsStatus === 'FIRM',
               'bg-yellow-50 border-yellow-200': changeResolveJob.clientSmsStatus === 'CHANGED',
               'bg-red-50 border-red-200': changeResolveJob.clientSmsStatus === 'CANCELLED',
             }">
          <p class="text-xs text-gray-500 mb-1">Client message</p>
          <p class="text-sm text-gray-800 italic">"{{ changeResolveJob.clientSmsMessage }}"</p>
        </div>
        <div class="flex space-x-3">
          <button @click="changeResolveModal = false; changeResolveJob = null" class="flex-1 h-10 rounded-lg border border-gray-300 text-gray-600 font-medium hover:bg-gray-50">Cancel</button>
          <button @click="resolveChangeRequest()" class="flex-1 h-10 rounded-lg bg-primary text-white font-medium hover:opacity-90">Mark as Resolved</button>
        </div>
      </div>
    </div>

    <!-- Bulk SMS Modal -->
    <div v-if="bulkSmsModal" class="w-full z-50 h-screen fixed top-0 left-0 bg-secondary bg-opacity-50 flex items-center justify-center" style="backdrop-filter:blur(2px)">
      <div class="bg-white rounded-xl shadow-2xl w-full max-w-3xl mx-4 flex flex-col" style="max-height:92vh">
        <!-- Header -->
        <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div>
            <h3 class="text-lg font-bold text-gray-900">
              <i class="fa-solid fa-comments text-indigo-600 mr-2"></i>Bulk Client SMS
            </h3>
            <p class="text-xs text-gray-400 mt-0.5">Send confirmation messages to clients on today's schedule</p>
          </div>
          <button @click="closeBulkSms()" class="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <!-- Body -->
        <div class="flex-1 overflow-y-auto" style="min-height:0">
          <!-- Compose -->
          <div v-if="!bulkSmsSending && !bulkSmsDone">
            <div class="grid grid-cols-12 divide-x divide-gray-200" style="min-height:400px">
              <!-- Left: Settings & Message -->
              <div class="col-span-7 px-6 py-5">
                <!-- Recipient type -->
                <div class="mb-5">
                  <label class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 block text-left">Recipient Type</label>
                  <div class="flex gap-2">
                    <button @click="bulkSmsRecipientType = 'primary'"
                      class="px-4 py-2 rounded-lg text-xs font-medium transition-colors border"
                      :class="bulkSmsRecipientType === 'primary' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'">
                      <i class="fa-solid fa-user mr-1.5"></i>Primary
                    </button>
                    <button @click="bulkSmsRecipientType = 'secondary'"
                      class="px-4 py-2 rounded-lg text-xs font-medium transition-colors border"
                      :class="bulkSmsRecipientType === 'secondary' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'">
                      <i class="fa-solid fa-user mr-1.5"></i>Secondary
                    </button>
                    <button @click="bulkSmsRecipientType = 'both'"
                      class="px-4 py-2 rounded-lg text-xs font-medium transition-colors border"
                      :class="bulkSmsRecipientType === 'both' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'">
                      <i class="fa-solid fa-users mr-1.5"></i>Both
                    </button>
                  </div>
                  <p class="text-xs text-gray-400 mt-1.5 text-left">{{ bulkSmsRecipientType === 'primary' ? 'Messages will be sent to primary contact phone numbers' : bulkSmsRecipientType === 'secondary' ? 'Messages will be sent to secondary contact phone numbers' : 'Messages will be sent to both primary and secondary contacts' }}</p>
                </div>

                <!-- Template selector -->
                <div v-if="visibleSmsTemplates.length > 0" class="mb-5">
                  <label class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 block text-left">Template</label>
                  <select v-model="selectedSmsTemplateId" @change="applySmsTemplate('bulkSmsMessage')"
                    class="w-full h-10 rounded-lg border border-gray-200 px-3 text-sm text-gray-700 outline-none focus:border-indigo-400 bg-white cursor-pointer">
                    <option value="">Custom message</option>
                    <option v-for="t in visibleSmsTemplates" :key="t.id" :value="t.id">{{ t.name }}</option>
                  </select>
                </div>

                <!-- Message template -->
                <div class="mb-5">
                  <label class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 block text-left">Message Template</label>
                  <textarea v-model="bulkSmsMessage" rows="5"
                    class="w-full rounded-lg border border-gray-200 p-3 text-sm text-gray-700 outline-none focus:border-indigo-400 transition-colors resize-none"></textarea>
                  <div class="mt-2">
                    <p class="text-xs text-gray-400 mb-1.5 text-left">Shortcodes — click to insert:</p>
                    <div class="flex flex-wrap gap-1.5">
                      <button v-for="sc in bulkSmsShortcodes" :key="sc.code" @click="insertShortcode(sc.code)"
                        class="inline-flex items-center px-2.5 py-1 rounded-md bg-indigo-50 text-indigo-700 text-xs font-medium hover:bg-indigo-100 transition-colors cursor-pointer">
                        <code class="mr-1">{{ sc.code }}</code>
                        <span class="text-indigo-400 font-normal">{{ sc.label }}</span>
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Settings -->
                <div>
                  <label class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 block text-left">Settings</label>
                  <div class="border border-gray-200 rounded-lg p-3">
                    <label class="flex items-center cursor-pointer select-none" @click.prevent="bulkSmsGroupByContact = !bulkSmsGroupByContact">
                      <span
                        class="relative inline-flex items-center flex-shrink-0 rounded-full transition-colors duration-200"
                        :class="bulkSmsGroupByContact ? 'bg-indigo-600' : 'bg-gray-300'"
                        style="width:40px;height:22px">
                        <span
                          class="inline-block bg-white rounded-full shadow transition-transform duration-200"
                          style="width:18px;height:18px"
                          :style="{ transform: bulkSmsGroupByContact ? 'translateX(20px)' : 'translateX(2px)' }">
                        </span>
                      </span>
                      <span class="ml-3 text-sm font-medium text-gray-700 text-left">Group by contact</span>
                    </label>
                    <p class="text-xs text-gray-400 mt-1 ml-12 text-left">{{ bulkSmsGroupByContact
                      ? 'One SMS per client — multiple jobs are combined into a single message'
                      : 'One SMS per job — clients with multiple jobs will receive separate texts' }}</p>
                  </div>
                </div>
              </div>

              <!-- Right: Recipients -->
              <div class="col-span-5 flex flex-col">
                <div class="px-4 py-3 border-b border-gray-200 flex-shrink-0">
                  <div class="flex items-center justify-between">
                    <label class="text-xs font-medium text-gray-500 uppercase tracking-wide text-left">
                      Recipients
                    </label>
                    <span class="text-xs font-semibold text-indigo-600">{{ bulkSmsSelectedCount }} / {{ bulkSmsRecipients.length }}</span>
                  </div>
                  <div class="flex items-center gap-3 mt-2">
                    <button @click="bulkSmsSelectAll()" class="text-xs text-indigo-600 hover:text-indigo-800 font-medium">Select All</button>
                    <span class="text-gray-300">|</span>
                    <button @click="bulkSmsDeselectAll()" class="text-xs text-gray-500 hover:text-gray-700 font-medium">Deselect All</button>
                  </div>
                  <div class="flex flex-wrap gap-1.5 mt-2">
                    <button v-for="st in ['ON-CALL','FIRM','COMMITTED','CANCELLED','COMPLETED']" :key="st"
                      @click="bulkSmsSelectByStatus(st)"
                      class="text-xs font-medium px-2 py-1 rounded-md border transition-colors cursor-pointer"
                      :class="{
                        'bg-yellow-50 text-yellow-700 border-yellow-200 hover:bg-yellow-100': st === 'ON-CALL',
                        'bg-green-50 text-green-700 border-green-200 hover:bg-green-100': st === 'FIRM',
                        'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100': st === 'COMMITTED',
                        'bg-red-50 text-red-700 border-red-200 hover:bg-red-100': st === 'CANCELLED',
                        'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100': st === 'COMPLETED',
                      }">
                      {{ st }}
                    </button>
                  </div>
                </div>
                <div class="flex-1 overflow-y-auto">
                  <div v-if="bulkSmsRecipients.length === 0" class="flex flex-col items-center justify-center h-full py-10 px-4">
                    <i class="fa-solid fa-users-slash text-gray-300 text-2xl mb-3"></i>
                    <p class="text-sm text-gray-400 text-center">No recipients found</p>
                    <p class="text-xs text-gray-300 text-center mt-1">Make sure jobs are loaded on the schedule with phone numbers</p>
                  </div>
                  <label v-for="(r, idx) in bulkSmsRecipients" :key="idx"
                    class="flex items-start px-4 py-3 cursor-pointer transition-colors border-b border-gray-100"
                    :class="r.selected ? 'bg-indigo-50 hover:bg-indigo-100' : 'hover:bg-gray-50'">
                    <input type="checkbox" v-model="r.selected" class="w-4 h-4 rounded border-gray-300 text-indigo-600 cursor-pointer mt-0.5 flex-shrink-0" />
                    <div class="ml-3 flex-1 min-w-0">
                      <p class="text-sm font-medium text-gray-800 truncate text-left">{{ r.contact || r.company || 'Unknown' }}</p>
                      <p v-if="r.company && r.contact" class="text-xs text-gray-400 truncate text-left">{{ r.company }}</p>
                      <div class="flex items-center gap-3 mt-1">
                        <span class="text-xs text-gray-500 text-left"><i class="fa-solid fa-phone text-gray-300 mr-1" style="font-size:9px"></i>{{ r.phone }}</span>
                        <span class="text-xs text-gray-400">{{ r.jobCount }} {{ r.jobCount === 1 ? 'job' : 'jobs' }}</span>
                      </div>
                    </div>
                    <div class="flex-shrink-0 ml-2 mt-0.5 flex flex-wrap gap-1 justify-end">
                      <span v-if="!r.phone" class="text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-600">No phone</span>
                      <template v-else>
                        <span v-for="st in [...new Set(r.jobs.map(j => j.status))].filter(Boolean)" :key="st"
                          class="text-xs font-medium px-1.5 py-0.5 rounded-full"
                          :class="{
                            'bg-yellow-100 text-yellow-700': st === 'ON-CALL',
                            'bg-green-100 text-green-700': st === 'FIRM',
                            'bg-blue-100 text-blue-700': st === 'COMMITTED',
                            'bg-red-100 text-red-700': st === 'CANCELLED',
                            'bg-gray-100 text-gray-600': st === 'COMPLETED',
                          }">{{ st }}</span>
                      </template>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <!-- Sending progress -->
          <div v-if="bulkSmsSending && !bulkSmsDone" class="py-16 px-6">
            <div class="flex flex-col items-center max-w-sm mx-auto">
              <div class="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center mb-4">
                <i class="fa-solid fa-paper-plane text-indigo-600 text-2xl animate-pulse"></i>
              </div>
              <p class="text-sm font-semibold text-gray-800 mb-1">Sending messages...</p>
              <p class="text-xs text-gray-400 mb-4">{{ bulkSmsSent }} of {{ bulkSmsTotal }} sent</p>
              <div class="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div class="bg-indigo-600 h-3 rounded-full transition-all duration-300"
                  :style="{ width: bulkSmsProgress + '%' }"></div>
              </div>
              <p class="text-xs text-gray-400 mt-2">{{ bulkSmsProgress }}% complete</p>
            </div>
          </div>

          <!-- Results -->
          <div v-if="bulkSmsDone" class="py-10 px-6">
            <div class="flex flex-col items-center mb-6">
              <div class="w-16 h-16 rounded-full flex items-center justify-center mb-3"
                :class="bulkSmsFailedCount > 0 ? 'bg-yellow-100' : 'bg-green-100'">
                <i class="text-2xl" :class="bulkSmsFailedCount > 0 ? 'fa-solid fa-triangle-exclamation text-yellow-600' : 'fa-solid fa-circle-check text-green-600'"></i>
              </div>
              <p class="text-lg font-bold text-gray-900">
                {{ bulkSmsFailedCount > 0 ? 'Sent with errors' : 'All messages sent!' }}
              </p>
              <p class="text-sm text-gray-500">{{ bulkSmsSuccessCount }} sent{{ bulkSmsFailedCount > 0 ? ', ' + bulkSmsFailedCount + ' failed' : '' }}</p>
            </div>

            <div class="max-w-lg mx-auto border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-52 overflow-y-auto">
              <div v-for="(r, idx) in bulkSmsResults" :key="idx"
                class="flex items-center px-4 py-2.5 text-sm">
                <i :class="r.success ? 'fa-solid fa-circle-check text-green-500' : 'fa-solid fa-circle-xmark text-red-500'" class="mr-3"></i>
                <span class="text-gray-700 flex-1 truncate text-left">{{ r.phone }}</span>
                <span v-if="r.success" class="text-xs text-green-600 font-medium">Sent</span>
                <span v-else class="text-xs text-red-600 font-medium truncate" style="max-width:160px">{{ r.error || 'Failed' }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="px-6 py-4 border-t border-gray-200 flex-shrink-0">
          <div v-if="!bulkSmsSending && !bulkSmsDone" class="flex items-center justify-between">
            <div class="text-xs text-gray-400 text-left">
              <i class="fa-solid fa-info-circle mr-1"></i>
              Messages sent via Twilio to client phone numbers
            </div>
            <div class="flex items-center gap-2">
              <button @click="closeBulkSms()"
                class="px-4 h-10 rounded-lg text-sm font-medium bg-white text-gray-600 border border-gray-200 hover:bg-gray-100 transition-colors">
                Cancel
              </button>
              <button @click="sendBulkSms()"
                :disabled="bulkSmsSelectedCount === 0 || !bulkSmsMessage.trim()"
                class="px-5 h-10 rounded-lg text-sm font-medium inline-flex items-center whitespace-nowrap bg-indigo-600 text-white hover:bg-indigo-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                <i class="fa-solid fa-paper-plane mr-2" style="font-size:11px"></i>
                Send {{ bulkSmsSelectedCount }} {{ bulkSmsSelectedCount === 1 ? 'Message' : 'Messages' }}
              </button>
            </div>
          </div>
          <div v-else-if="bulkSmsDone" class="flex justify-end">
            <button @click="closeBulkSms()"
              class="px-5 h-10 rounded-lg text-sm font-medium bg-gray-900 text-white hover:opacity-90 transition-colors">
              Done
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Column Settings Modal -->
    <div v-if="showColumnSettings" class="w-full z-50 h-screen fixed top-0 left-0 bg-secondary bg-opacity-50">
      <div class="w-full h-full relative flex items-center justify-center">
        <div class="w-96 max-h-screen overflow-y-auto relative py-6 px-6 rounded-lg bg-white shadow-lg">
          <div class="absolute top-0 right-0 m-3">
            <i @click="showColumnSettings = false"
              class="fa-solid fa-xmark cursor-pointer text-secondary hover:text-primary"></i>
          </div>
          <h3 class="text-lg font-bold text-gray-900 mb-4">
            <i class="fa-solid fa-gear text-gray-500 mr-2"></i>Column Settings
          </h3>

          <div class="space-y-1">
            <div v-for="(col, cIdx) in modalColumnConfig" :key="'colcfg-' + col.field"
              class="flex items-center py-2 px-3 rounded hover:bg-gray-100">
              <input type="checkbox" v-model="col.visible" class="w-4 h-4 cursor-pointer flex-shrink-0" />
              <span class="ml-3 text-sm text-gray-700 flex-grow truncate">{{ col.label }}</span>
              <div class="flex items-center space-x-1 mr-2 flex-shrink-0">
                <button v-for="s in ['S', 'M', 'L']" :key="s"
                  @click="setColSize(col.field, s)"
                  class="w-7 h-7 rounded text-xs font-bold transition-colors"
                  :class="col.size === s ? 'bg-primary text-white' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'">
                  {{ s }}
                </button>
              </div>
            </div>
          </div>

          <div class="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
            <button @click="resetColumnConfig()"
              class="text-sm text-gray-500 hover:text-gray-700">
              Reset to Default
            </button>
            <button @click="saveColumnConfig()"
              class="h-10 px-5 bg-primary text-white text-sm rounded hover:bg-secondary">
              Save
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Start Times Modal -->
    <div v-if="showStartTimes" class="w-full z-50 h-screen fixed top-0 left-0 bg-secondary bg-opacity-50" style="backdrop-filter:blur(2px)">
      <div class="w-full h-full relative flex items-center justify-center">
        <div class="w-full max-w-lg mx-4 relative rounded-xl bg-white shadow-2xl flex flex-col" style="max-height:80vh">
          <!-- Header -->
          <div class="flex items-center justify-between px-6 py-5 border-b border-gray-200 flex-shrink-0">
            <h3 class="text-lg font-bold text-gray-900">
              <i class="fa-solid fa-clock text-purple-600 mr-2"></i>Tester Start Times
            </h3>
            <button @click="showStartTimes = false"
              class="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors">
              <i class="fa-solid fa-xmark"></i>
            </button>
          </div>

          <!-- Body -->
          <div class="flex-1 overflow-y-auto px-6 py-4" style="min-height:0">
            <div v-if="startTimeEntries.length === 0" class="text-sm text-gray-400 py-8 text-center">
              No testers found.
            </div>

            <div class="space-y-3">
              <div v-for="(entry, eIdx) in startTimeEntries" :key="'st-' + eIdx"
                class="rounded-lg border px-5 py-4 transition-colors"
                :class="entry.dayOff ? 'border-gray-100 bg-gray-50' : 'border-gray-200 bg-white'">
                <div class="flex items-center justify-between">
                  <span class="text-sm font-semibold" :class="entry.dayOff ? 'text-gray-400 line-through' : 'text-gray-800'">{{ entry.testerName }}</span>
                  <div class="flex items-center space-x-3">
                    <input type="time" v-model="entry.startTime"
                      :disabled="entry.dayOff"
                      class="h-9 px-3 rounded-lg border text-sm text-gray-700 outline-none focus:border-purple-400 transition-colors"
                      :class="entry.dayOff ? 'border-gray-100 bg-gray-100 opacity-40 cursor-not-allowed' : 'border-gray-200 bg-gray-50'" />
                    <label class="flex items-center cursor-pointer select-none whitespace-nowrap" @click.prevent="entry.dayOff = !entry.dayOff">
                      <span
                        class="relative inline-flex items-center flex-shrink-0 rounded-full transition-colors duration-200"
                        :class="entry.dayOff ? 'bg-gray-400' : 'bg-green-500'"
                        style="width:36px;height:20px">
                        <span
                          class="inline-block bg-white rounded-full shadow transition-transform duration-200"
                          style="width:16px;height:16px"
                          :style="{ transform: entry.dayOff ? 'translateX(18px)' : 'translateX(2px)' }">
                        </span>
                      </span>
                      <span class="ml-2 text-xs font-medium" :class="entry.dayOff ? 'text-gray-400' : 'text-green-600'">{{ entry.dayOff ? 'Day off' : 'Working' }}</span>
                    </label>
                  </div>
                </div>
                <div v-if="!entry.dayOff && (entry.firstJobAddress || entry.firstJobTime)" class="mt-2 pt-2 border-t border-gray-100">
                  <div v-if="entry.firstJobAddress" class="text-xs text-gray-400">
                    <i class="fa-solid fa-location-dot text-gray-300 mr-1"></i>{{ entry.firstJobAddress }}
                  </div>
                  <div v-if="entry.firstJobTime" class="text-xs text-gray-400 mt-1">
                    <i class="fa-regular fa-clock text-gray-300 mr-1"></i>First job at {{ entry.firstJobTime }}
                  </div>
                </div>
                <div v-if="entry.dayOff" class="mt-2 text-xs text-gray-400">
                  <i class="fa-solid fa-umbrella-beach text-gray-300 mr-1"></i>No jobs scheduled
                </div>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="px-6 py-4 border-t border-gray-200 flex-shrink-0 flex justify-end">
            <button @click="saveStartTimes()"
              class="h-10 px-6 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors">
              Save Start Times
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Contractor start time banner -->
    <div v-if="currentUser && currentUser.role === 'contractor' && !isTeamLeader && contractorStartTime"
      class="w-full bg-purple-600 text-white text-center py-3 text-sm font-semibold">
      <i class="fa-solid fa-clock mr-2"></i>Your start time today: {{ formatStartTime(contractorStartTime) }}
    </div>

    <!-- Contractor tabs: Current / Completed -->
    <div v-if="currentUser && currentUser.role === 'contractor' && !isTeamLeader" class="w-full bg-white border-b border-gray-200 px-4">
      <div class="flex space-x-1">
        <button @click="contractorTab = 'current'"
          :class="contractorTab === 'current' ? 'border-indigo-500 text-indigo-600 bg-indigo-50' : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'"
          class="px-4 py-2.5 text-sm font-medium border-b-2 transition-colors rounded-t-lg">
          <i class="fa-solid fa-briefcase mr-1.5"></i>Current Jobs
        </button>
        <button @click="contractorTab = 'completed'"
          :class="contractorTab === 'completed' ? 'border-indigo-500 text-indigo-600 bg-indigo-50' : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'"
          class="px-4 py-2.5 text-sm font-medium border-b-2 transition-colors rounded-t-lg">
          <i class="fa-solid fa-circle-check mr-1.5"></i>Completed Jobs
        </button>
      </div>
    </div>

    <!-- Card view for contractors (always shown, replaces table) -->
    <div v-if="currentUser && currentUser.role === 'contractor' && !isTeamLeader">
      <div v-if="contractorFilteredRows.filter(r => r.visible).length === 0" class="text-gray-400 text-sm w-full py-20 flex justify-center">
        {{ contractorTab === 'current' ? 'No current jobs' : 'No completed jobs' }}
      </div>
      <div v-for="(i, index) in contractorFilteredRows" :key="'mobile-' + index" v-show="i.visible" @click="showWorkCard(i)" class="bg-white border-b border-gray-200 px-4 py-3 cursor-pointer active:bg-gray-50">
        <div class="flex items-start justify-between mb-2">
          <div>
            <div class="text-sm font-bold text-gray-900">{{ i.projectName || i.company }}</div>
            <div v-if="i.projectName && i.company" class="text-xs text-gray-500">{{ i.company }}</div>
          </div>
          <span class="text-xs font-bold px-2 py-1 rounded-full"
            :class="{
              'bg-yellow-100 text-yellow-700': i.status === 'ON-CALL',
              'bg-green-100 text-green-700': i.status === 'FIRM' || i.status === 'COMMITTED',
              'bg-green-500 text-white': i.status === 'COMPLETED',
              'bg-red-500 text-white': i.status === 'CANCELLED',
            }">{{ i.status }}</span>
        </div>
        <div class="space-y-1 mb-3">
          <div v-if="i.date" class="flex items-center text-xs text-gray-600">
            <i class="fa-regular fa-calendar w-4 mr-1.5 text-gray-400"></i>{{ i.date }}
            <span v-if="i.time" class="ml-2">{{ $moment(i.time).format('h:mmA') }}</span>
          </div>
          <div v-if="i.fullAddress" class="flex items-center text-xs text-gray-600">
            <i class="fa-solid fa-location-dot w-4 mr-1.5 text-gray-400"></i>{{ i.fullAddress }}
          </div>
          <div v-for="f in testerFields(i)" :key="f.key" class="flex items-center text-xs text-gray-600">
            <i class="fa-solid fa-circle-info w-4 mr-1.5 text-gray-400"></i>{{ f.label }}: {{ f.value }}
          </div>
          <div v-if="!customFieldsEnabled && i.meters" class="flex items-center text-xs text-gray-600">
            <i class="fa-solid fa-ruler w-4 mr-1.5 text-gray-400"></i>{{ i.meters }}m³
            <span v-if="i.mixCode" class="ml-3"><i class="fa-solid fa-flask w-4 mr-1 text-gray-400"></i>{{ i.mixCode }}</span>
          </div>
          <div v-if="i.requirements" class="flex items-center text-xs text-gray-600">
            <i class="fa-solid fa-list-check w-4 mr-1.5 text-gray-400"></i>{{ i.requirements }}
          </div>
          <div v-if="!customFieldsEnabled && i.pourLocation" class="flex items-center text-xs text-gray-600">
            <i class="fa-solid fa-map-pin w-4 mr-1.5 text-gray-400"></i>{{ i.pourLocation }}
          </div>
          <div v-if="i.contact" class="flex items-center text-xs text-gray-600">
            <i class="fa-solid fa-user w-4 mr-1.5 text-gray-400"></i>{{ i.jobContact || i.contact }}
            <span v-if="i.jobPhone || i.phone" class="ml-2"><i class="fa-solid fa-phone mr-1 text-gray-400" style="font-size:9px"></i>{{ i.jobPhone || i.phone }}</span>
          </div>
        </div>
        <!-- Current tab: action buttons -->
        <div v-if="contractorTab === 'current'" class="flex items-center gap-2">
          <button v-if="!i.enRoute" @click.stop="enRouteJob = i; enRouteModal = true"
            class="flex-1 text-xs font-bold h-10 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors">
            <i class="fa-solid fa-route mr-1.5"></i>En-Route
          </button>
          <span v-else class="flex-1 inline-flex items-center justify-center h-10 rounded-lg text-xs font-medium bg-yellow-100 text-yellow-700">
            <i class="fa-solid fa-route mr-1.5"></i>En-Route Sent
          </span>
          <button @click.stop="signOff = rowData.indexOf(i)"
            class="flex-1 text-xs font-bold h-10 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors">
            <i class="fa-solid fa-clipboard-check mr-1.5"></i>Sign Off
          </button>
        </div>
        <!-- Completed tab: status display -->
        <div v-else class="flex items-center">
          <span v-if="i.status === 'COMPLETED'" class="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold bg-green-500 text-white">
            <i class="fa-solid fa-circle-check mr-1"></i>COMPLETED
          </span>
          <span v-else-if="i.status === 'CANCELLED'" class="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold bg-red-500 text-white">
            <i class="fa-solid fa-circle-xmark mr-1"></i>CANCELLED
          </span>
        </div>
      </div>
    </div>

    <!-- ======== INVITE NOTIFICATION MODAL ======== -->
    <div v-if="currentInvite" class="w-full z-50 h-screen fixed top-0 left-0 bg-black bg-opacity-60" style="backdrop-filter:blur(3px)">
      <div class="w-full h-full flex items-center justify-center px-4">
        <div class="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
          <!-- Header -->
          <div class="bg-indigo-600 px-6 py-5 text-white">
            <div class="flex items-center mb-1">
              <i class="fa-solid fa-bell text-yellow-300 mr-2.5 text-lg"></i>
              <h3 class="text-lg font-bold">New Job Invitation</h3>
            </div>
            <p class="text-indigo-200 text-sm">You've been invited to manage a job.</p>
          </div>
          <!-- Job details -->
          <div class="px-6 py-5">
            <div class="bg-gray-50 rounded-xl p-4 space-y-2.5 mb-5">
              <div v-if="currentInvite.company" class="flex items-center text-sm">
                <i class="fa-solid fa-building w-5 text-gray-400 mr-2" style="font-size:12px"></i>
                <span class="font-semibold text-gray-900">{{ currentInvite.company }}</span>
              </div>
              <div v-if="currentInvite.projectName" class="flex items-center text-sm text-gray-700">
                <i class="fa-solid fa-folder w-5 text-gray-400 mr-2" style="font-size:12px"></i>
                {{ currentInvite.projectName }}
              </div>
              <div v-if="currentInvite.date" class="flex items-center text-sm text-gray-700">
                <i class="fa-regular fa-calendar w-5 text-gray-400 mr-2" style="font-size:12px"></i>
                {{ formatInviteDate(currentInvite.date) }}
              </div>
              <div v-if="currentInvite.fullAddress" class="flex items-center text-sm text-gray-700">
                <i class="fa-solid fa-location-dot w-5 text-gray-400 mr-2" style="font-size:12px"></i>
                {{ currentInvite.fullAddress }}
              </div>
              <!-- Older invites predate customFields and only carry `meters`. -->
              <div v-for="f in inviteFields" :key="f.label" class="flex items-center text-sm text-gray-700">
                <i class="fa-solid fa-circle-info w-5 text-gray-400 mr-2" style="font-size:12px"></i>
                {{ f.label }}: {{ f.value }}
              </div>
              <div v-if="!inviteFields.length && currentInvite.meters" class="flex items-center text-sm text-gray-700">
                <i class="fa-solid fa-ruler w-5 text-gray-400 mr-2" style="font-size:12px"></i>
                {{ currentInvite.meters }}m³
              </div>
              <div v-if="currentInvite.tester" class="flex items-center text-sm text-gray-700">
                <i class="fa-solid fa-hard-hat w-5 text-gray-400 mr-2" style="font-size:12px"></i>
                {{ currentInvite.tester }}
              </div>
            </div>
            <p class="text-sm text-gray-600 mb-5">You can now assign your team members to this job.</p>
            <div class="flex gap-3">
              <button @click="rejectInvite()"
                class="flex-1 h-11 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors">
                Reject
              </button>
              <button @click="acknowledgeInvite()"
                class="flex-1 h-11 text-sm font-bold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 transition-colors">
                Acknowledge
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ======== TEAM LEADER VIEW ======== -->

    <!-- Invite Team Leader Modal -->
    <div v-if="inviteModalJob" class="w-full z-50 h-screen fixed top-0 left-0 bg-secondary bg-opacity-50" style="backdrop-filter:blur(2px)">
      <div class="w-full h-full relative flex items-center justify-center">
        <div class="w-96 relative py-8 px-6 rounded-xl bg-white shadow-2xl">
          <div class="absolute top-0 right-0 m-3">
            <i @click="inviteModalJob = null" class="fa-solid fa-xmark cursor-pointer text-gray-400 hover:text-gray-600"></i>
          </div>
          <h3 class="text-lg font-bold text-gray-900 mb-4"><i class="fa-solid fa-user-plus text-indigo-600 mr-2"></i>Invite Team Leader</h3>
          <div v-if="availableTeamLeadersForInvite.length === 0" class="text-sm text-gray-500 py-4 text-center">No other team leaders available to invite.</div>
          <div v-else class="space-y-1.5 max-h-60 overflow-y-auto">
            <div v-for="tl in availableTeamLeadersForInvite" :key="tl.id"
              @click="inviteTeamLeaderToJob(tl)"
              class="w-full rounded-lg px-4 h-10 cursor-pointer hover:bg-indigo-50 flex items-center text-sm text-gray-700 border border-gray-100">
              <i class="fa-solid fa-star text-yellow-500 mr-2" style="font-size:10px"></i>{{ tl.name }}
              <span v-if="tl.region" class="text-xs text-gray-400 ml-1">({{ tl.region }})</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Team leader job cards (mobile portrait only) -->
    <div v-if="isTeamLeader" class="sm:hidden">
      <div v-if="teamLeaderFilteredRows.length === 0" class="text-gray-400 text-sm w-full py-20 flex justify-center">
        No jobs
      </div>
      <div v-for="(i, index) in teamLeaderFilteredRows" :key="'tl-' + index"
        @click="showWorkCard(i)"
        :class="[
          i.status == 'ON-CALL' ? 'bg-yellow-50' : 'bg-white',
          i.status == 'COMPLETED' ? 'bg-green-200' : '',
          i.status == 'CANCELLED' ? 'bg-red-200' : '',
        ]"
        class="border-b border-gray-200 px-4 py-3 cursor-pointer active:bg-gray-50">
        <div class="flex items-start justify-between mb-2">
          <div>
            <div class="text-sm font-bold text-gray-900">{{ i.projectName || i.company }}</div>
            <div v-if="i.projectName && i.company" class="text-xs text-gray-500">{{ i.company }}</div>
          </div>
          <!-- Status dropdown (editable) -->
          <select v-model="i.status" @click.stop @change="updateTeamLeaderJobStatus(i)"
            class="text-xs font-bold px-2 py-1 rounded-full border-0 outline-none cursor-pointer"
            :class="{
              'bg-yellow-100 text-yellow-700': i.status === 'ON-CALL',
              'bg-green-100 text-green-700': i.status === 'FIRM' || i.status === 'COMMITTED',
              'bg-green-500 text-white': i.status === 'COMPLETED',
              'bg-red-500 text-white': i.status === 'CANCELLED',
            }">
            <option>ON-CALL</option>
            <option>FIRM</option>
            <option>COMMITTED</option>
            <option>COMPLETED</option>
            <option>CANCELLED</option>
          </select>
        </div>
        <div class="space-y-1 mb-3">
          <div v-if="i.date" class="flex items-center text-xs text-gray-600">
            <i class="fa-regular fa-calendar w-4 mr-1.5 text-gray-400"></i>{{ i.date }}
            <span v-if="i.time" class="ml-2">{{ $moment(i.time).format('h:mmA') }}</span>
          </div>
          <div v-if="i.fullAddress" class="flex items-center text-xs text-gray-600">
            <i class="fa-solid fa-location-dot w-4 mr-1.5 text-gray-400"></i>{{ i.fullAddress }}
          </div>
          <div v-for="f in testerFields(i)" :key="f.key" class="flex items-center text-xs text-gray-600">
            <i class="fa-solid fa-circle-info w-4 mr-1.5 text-gray-400"></i>{{ f.label }}: {{ f.value }}
          </div>
          <div v-if="!customFieldsEnabled && i.meters" class="flex items-center text-xs text-gray-600">
            <i class="fa-solid fa-ruler w-4 mr-1.5 text-gray-400"></i>{{ i.meters }}m³
            <span v-if="i.mixCode" class="ml-3"><i class="fa-solid fa-flask w-4 mr-1 text-gray-400"></i>{{ i.mixCode }}</span>
          </div>
          <div v-if="i.requirements" class="flex items-center text-xs text-gray-600">
            <i class="fa-solid fa-list-check w-4 mr-1.5 text-gray-400"></i>{{ i.requirements }}
          </div>
          <div v-if="!customFieldsEnabled && i.pourLocation" class="flex items-center text-xs text-gray-600">
            <i class="fa-solid fa-map-pin w-4 mr-1.5 text-gray-400"></i>{{ i.pourLocation }}
          </div>
          <div v-if="i.contact" class="flex items-center text-xs text-gray-600">
            <i class="fa-solid fa-user w-4 mr-1.5 text-gray-400"></i>{{ i.jobContact || i.contact }}
            <span v-if="i.jobPhone || i.phone" class="ml-2"><i class="fa-solid fa-phone mr-1 text-gray-400" style="font-size:9px"></i>{{ i.jobPhone || i.phone }}</span>
          </div>
          <!-- Tester name display -->
          <div class="flex items-center text-xs text-gray-600">
            <i class="fa-solid fa-hard-hat w-4 mr-1.5 text-gray-400"></i>
            <span v-if="i.tester">{{ i.tester }}</span>
            <span v-else class="text-red-400 font-medium">Unassigned</span>
          </div>
          <!-- Team leaders on this job -->
          <div v-if="i.teamLeaders && i.teamLeaders.length" class="flex items-center text-xs text-gray-600 flex-wrap gap-1">
            <i class="fa-solid fa-star w-4 mr-0.5 text-yellow-500" style="font-size:9px"></i>
            <span v-for="tl in i.teamLeaders" :key="tl.id" class="inline-flex items-center px-1.5 py-0.5 rounded bg-yellow-50 text-yellow-700 text-xs">{{ tl.name }}</span>
          </div>
        </div>
        <!-- Tester assignment + notes row -->
        <div @click.stop class="flex items-center gap-2 mb-2">
          <div class="flex items-center gap-2 flex-1">
            <select :value="i.testerId || ''" @change="assignTeamMemberToJob(i, $event.target.value)"
              class="h-8 text-xs bg-gray-100 border border-gray-200 rounded-lg px-2 outline-none cursor-pointer flex-1">
              <option value="">Assign tester...</option>
              <option v-for="m in teamMembers" :key="m.id" :value="m.id">{{ m.name }}</option>
            </select>
          </div>
          <div class="flex items-center gap-1 flex-1">
            <input type="text" v-model="i.notes" @change="saveTeamLeaderJobNotes(i)" placeholder="Add note..."
              class="h-8 text-xs bg-gray-100 border border-gray-200 rounded-lg px-2 outline-none flex-1" />
          </div>
        </div>
        <!-- Action buttons -->
        <div @click.stop class="flex items-center gap-2">
          <template v-if="i.status !== 'COMPLETED' && i.status !== 'CANCELLED'">
            <button v-if="!i.enRoute" @click="enRouteJob = i; enRouteModal = true"
              class="flex-1 text-xs font-bold h-9 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors">
              <i class="fa-solid fa-route mr-1.5"></i>En-Route
            </button>
            <span v-else class="flex-1 inline-flex items-center justify-center h-9 rounded-lg text-xs font-medium bg-yellow-100 text-yellow-700">
              <i class="fa-solid fa-route mr-1.5"></i>En-Route Sent
            </span>
            <button @click="signOff = rowData.indexOf(i)"
              class="flex-1 text-xs font-bold h-9 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors">
              <i class="fa-solid fa-clipboard-check mr-1.5"></i>Sign Off
            </button>
            <button @click="inviteModalJob = i"
              class="h-9 px-3 text-xs font-medium bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors">
              <i class="fa-solid fa-user-plus mr-1"></i>Invite
            </button>
          </template>
          <template v-else>
            <span v-if="i.status === 'COMPLETED'" class="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold bg-green-500 text-white">
              <i class="fa-solid fa-circle-check mr-1"></i>COMPLETED
            </span>
            <span v-else-if="i.status === 'CANCELLED'" class="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold bg-red-500 text-white">
              <i class="fa-solid fa-circle-xmark mr-1"></i>CANCELLED
            </span>
          </template>
        </div>
      </div>
    </div>

    <!-- Team leader job table (landscape / tablet / desktop) -->
    <div v-if="isTeamLeader" class="hidden sm:block w-full bg-white pt-4">
      <div class="w-full px-3 pb-3 flex justify-end">
        <router-link :to="{ name: 'Tracker' }"
          class="text-xs font-bold px-3 h-8 bg-secondary text-white rounded-lg hover:bg-primary transition-colors flex items-center">
          <i class="fa-solid fa-location-crosshairs mr-2"></i>Team Tracker
        </router-link>
      </div>
      <div class="w-full overflow-x-auto">
      <div v-if="teamLeaderFilteredRows.length === 0" class="text-gray-400 text-sm w-full py-20 flex justify-center">
        No jobs
      </div>
      <table v-else class="border-collapse w-full" style="table-layout: fixed">
        <thead>
          <tr>
            <th v-if="tlColShown.datetime" :style="tlColStyle('datetime')" class="sticky top-0 z-10 bg-secondary text-gray-400 border-r border-gray-600 font-normal h-12 text-xs px-3 whitespace-nowrap">Date / Time</th>
            <th v-if="tlColShown.project" :style="tlColStyle('project')" class="sticky top-0 z-10 bg-secondary text-gray-400 border-r border-gray-600 font-normal h-12 text-xs px-3 whitespace-nowrap">
              <div class="flex items-center justify-center space-x-1">
                <span>Project</span>
                <i @click="openFilterDropdown($event, colDef('customer'))" class="fa-solid fa-filter text-xs opacity-60 cursor-pointer hover:opacity-100"></i>
              </div>
            </th>
            <th v-if="tlColShown.address" :style="tlColStyle('address')" class="sticky top-0 z-10 bg-secondary text-gray-400 border-r border-gray-600 font-normal h-12 text-xs px-3 whitespace-nowrap">Address</th>
            <th v-if="tlColShown.mix" :style="tlColStyle('mix')" class="sticky top-0 z-10 bg-secondary text-gray-400 border-r border-gray-600 font-normal h-12 text-xs px-3 whitespace-nowrap">{{ customFieldsEnabled ? 'Details' : 'Mix / m³' }}</th>
            <th v-if="tlColShown.requirements" :style="tlColStyle('requirements')" class="sticky top-0 z-10 bg-secondary text-gray-400 border-r border-gray-600 font-normal h-12 text-xs px-3 whitespace-nowrap">Requirements</th>
            <th v-if="tlColShown.contact" :style="tlColStyle('contact')" class="sticky top-0 z-10 bg-secondary text-gray-400 border-r border-gray-600 font-normal h-12 text-xs px-3 whitespace-nowrap">Contact</th>
            <th v-if="tlColShown.tester" :style="tlColStyle('tester')" class="sticky top-0 z-10 bg-secondary text-gray-400 border-r border-gray-600 font-normal h-12 text-xs px-3 whitespace-nowrap">
              <div class="flex items-center justify-center space-x-1">
                <span>Tester</span>
                <i @click="openFilterDropdown($event, colDef('tester'))" class="fa-solid fa-filter text-xs opacity-60 cursor-pointer hover:opacity-100"></i>
              </div>
            </th>
            <th v-if="tlColShown.teamLeader" :style="tlColStyle('teamLeader')" class="sticky top-0 z-10 bg-secondary text-gray-400 border-r border-gray-600 font-normal h-12 text-xs px-3 whitespace-nowrap">
              <div class="flex items-center justify-center space-x-1">
                <span>Team Leaders</span>
                <i @click="openFilterDropdown($event, colDef('teamLeader'))" class="fa-solid fa-filter text-xs opacity-60 cursor-pointer hover:opacity-100"></i>
              </div>
            </th>
            <th v-if="tlColShown.status" :style="tlColStyle('status')" class="sticky top-0 z-10 bg-secondary text-gray-400 border-r border-gray-600 font-normal h-12 text-xs px-3 whitespace-nowrap">
              <div class="flex items-center justify-center space-x-1">
                <span>Status</span>
                <i @click="openFilterDropdown($event, colDef('status'))" class="fa-solid fa-filter text-xs opacity-60 cursor-pointer hover:opacity-100"></i>
              </div>
            </th>
            <th v-if="tlColShown.published" :style="tlColStyle('published')" class="sticky top-0 z-10 bg-secondary text-gray-400 border-r border-gray-600 font-normal h-12 text-xs px-3 whitespace-nowrap">Publish</th>
            <th v-if="tlColShown.notes" :style="tlColStyle('notes')" class="sticky top-0 z-10 bg-secondary text-gray-400 border-r border-gray-600 font-normal h-12 text-xs px-3 whitespace-nowrap">Notes</th>
            <th v-if="tlColShown.actions" :style="tlColStyle('actions')" class="sticky top-0 z-10 bg-secondary text-gray-400 border-r border-gray-600 font-normal h-12 text-xs px-3 whitespace-nowrap">Actions</th>
            <th @click="openColumnSettings()"
              class="sticky top-0 z-10 bg-secondary text-gray-400 cursor-pointer hover:bg-gray-700 font-normal h-12 text-xs px-3 whitespace-nowrap" style="width:40px">
              <i class="fa-solid fa-gear"></i>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(i, index) in teamLeaderFilteredRows" :key="'tl-row-' + index"
            @click="showWorkCard(i)"
            :class="[
              i.status == 'ON-CALL' ? 'bg-yellow-50' : 'bg-white',
              i.status == 'COMPLETED' ? 'bg-green-200' : '',
              i.status == 'CANCELLED' ? 'bg-red-200' : '',
            ]"
            class="border-b border-gray-200 hover:bg-blue-50 transition-colors cursor-pointer">
            <!-- Date / Time -->
            <td v-if="tlColShown.datetime" :style="tlColStyle('datetime')" class="px-3 h-12 text-xs text-gray-700 whitespace-nowrap">
              <div>{{ i.date }}</div>
              <div v-if="i.time" class="text-gray-500">{{ $moment(i.time).format('h:mmA') }}</div>
            </td>
            <!-- Project -->
            <td v-if="tlColShown.project" :style="tlColStyle('project')" class="px-3 h-12 text-xs">
              <div class="font-bold text-gray-900 truncate">{{ i.projectName || i.company }}</div>
              <div v-if="i.projectName && i.company" class="text-gray-500 truncate">{{ i.company }}</div>
            </td>
            <!-- Address -->
            <td v-if="tlColShown.address" :style="tlColStyle('address')" class="px-3 h-12 text-xs text-gray-700">
              <div class="truncate">{{ i.fullAddress }}</div>
              <div v-if="i.pourLocation" class="text-gray-500 truncate">
                <i class="fa-solid fa-map-pin mr-1 text-gray-400" style="font-size:9px"></i>{{ i.pourLocation }}
              </div>
            </td>
            <!-- Mix / meters -->
            <td v-if="tlColShown.mix" :style="tlColStyle('mix')" class="px-3 h-12 text-xs text-gray-700 whitespace-nowrap">
              <template v-if="customFieldsEnabled">
                <div v-for="f in testerFields(i)" :key="f.key" class="text-gray-500">{{ f.label }}: {{ f.value }}</div>
              </template>
              <template v-else>
                <div v-if="i.mixCode">{{ i.mixCode }}</div>
                <div v-if="i.meters" class="text-gray-500">{{ i.meters }}m&sup3;</div>
              </template>
            </td>
            <!-- Requirements -->
            <td v-if="tlColShown.requirements" :style="tlColStyle('requirements')" class="px-3 h-12 text-xs text-gray-700">
              <div class="truncate">{{ i.requirements }}</div>
            </td>
            <!-- Contact -->
            <td v-if="tlColShown.contact" :style="tlColStyle('contact')" class="px-3 h-12 text-xs text-gray-700">
              <div class="truncate">{{ i.jobContact || i.contact }}</div>
              <div v-if="i.jobPhone || i.phone" class="text-gray-500 truncate">{{ i.jobPhone || i.phone }}</div>
            </td>
            <!-- Tester (editable) -->
            <td v-if="tlColShown.tester" :style="tlColStyle('tester')" class="px-3 h-12" @click.stop>
              <div class="flex items-center gap-1">
                <select :value="i.testerId || ''" @change="assignTeamMemberToJob(i, $event.target.value)"
                  class="h-8 flex-1 min-w-0 text-xs bg-gray-100 border border-gray-200 rounded-lg px-2 outline-none cursor-pointer">
                  <option value="">Assign tester...</option>
                  <option v-for="m in teamMembers" :key="m.id" :value="m.id">{{ m.name }}</option>
                </select>
                <div v-if="i.tester" @click="openTesterSmsModal(i)"
                  class="h-8 px-2 flex-shrink-0 bg-primary hover:bg-secondary text-white rounded-lg flex items-center justify-center cursor-pointer" style="font-size: 9px">
                  SMS
                </div>
              </div>
            </td>
            <!-- Team leaders -->
            <td v-if="tlColShown.teamLeader" :style="tlColStyle('teamLeader')" class="px-3 h-12">
              <div v-if="i.teamLeaders && i.teamLeaders.length" class="flex flex-wrap gap-1">
                <span v-for="tl in i.teamLeaders" :key="tl.id" class="inline-flex items-center px-1.5 py-0.5 rounded bg-yellow-50 text-yellow-700 text-xs">{{ tl.name }}</span>
              </div>
              <span v-else class="text-xs text-gray-300">&mdash;</span>
            </td>
            <!-- Status (editable) -->
            <td v-if="tlColShown.status" :style="tlColStyle('status')" class="px-3 h-12" @click.stop>
              <select v-model="i.status" @change="updateTeamLeaderJobStatus(i)"
                class="text-xs font-bold w-full px-2 py-1 rounded-full border-0 outline-none cursor-pointer"
                :class="{
                  'bg-yellow-100 text-yellow-700': i.status === 'ON-CALL',
                  'bg-green-100 text-green-700': i.status === 'FIRM' || i.status === 'COMMITTED',
                  'bg-green-500 text-white': i.status === 'COMPLETED',
                  'bg-red-500 text-white': i.status === 'CANCELLED',
                }">
                <option>ON-CALL</option>
                <option>FIRM</option>
                <option>COMMITTED</option>
                <option>COMPLETED</option>
                <option>CANCELLED</option>
              </select>
            </td>
            <!-- Publish (editable) -->
            <td v-if="tlColShown.published" :style="tlColStyle('published')" class="px-1 h-12" @click.stop>
              <select @change="updateJob(rowData.indexOf(i))" v-model="i.published" :class="i.published
                  ? 'bg-green-100 text-green-700 border-green-200'
                  : 'bg-red-50 text-red-600 border-red-200'
                " class="text-xs outline-none text-center w-full h-10 rounded border cursor-pointer">
                <option :value="true">Published</option>
                <option :value="false">Unpublished</option>
              </select>
            </td>
            <!-- Notes (editable) -->
            <td v-if="tlColShown.notes" :style="tlColStyle('notes')" class="px-3 h-12" @click.stop>
              <input type="text" v-model="i.notes" @change="saveTeamLeaderJobNotes(i)" placeholder="Add note..."
                class="h-8 w-full text-xs bg-gray-100 border border-gray-200 rounded-lg px-2 outline-none" />
            </td>
            <!-- Actions -->
            <td v-if="tlColShown.actions" :style="tlColStyle('actions')" class="px-3 h-12 whitespace-nowrap" @click.stop>
              <template v-if="i.status !== 'COMPLETED' && i.status !== 'CANCELLED'">
                <div class="flex items-center gap-1.5">
                  <button v-if="!i.enRoute" @click="enRouteJob = i; enRouteModal = true"
                    class="text-xs font-bold px-3 h-8 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors">
                    <i class="fa-solid fa-route mr-1"></i>En-Route
                  </button>
                  <span v-else class="inline-flex items-center px-2 h-8 rounded-lg text-xs font-medium bg-yellow-100 text-yellow-700">
                    <i class="fa-solid fa-route mr-1"></i>Sent
                  </span>
                  <button @click="inviteModalJob = i"
                    class="text-xs font-medium px-3 h-8 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors">
                    <i class="fa-solid fa-user-plus mr-1"></i>Invite
                  </button>
                </div>
              </template>
              <template v-else>
                <span v-if="i.status === 'COMPLETED'" class="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold bg-green-500 text-white">
                  <i class="fa-solid fa-circle-check mr-1"></i>COMPLETED
                </span>
                <span v-else-if="i.status === 'CANCELLED'" class="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold bg-red-500 text-white">
                  <i class="fa-solid fa-circle-xmark mr-1"></i>CANCELLED
                </span>
              </template>
            </td>
            <!-- Spacer under the column settings header -->
            <td class="px-3 h-12" style="width:40px"></td>
          </tr>
        </tbody>
      </table>
      </div>
    </div>

    <!-- Service filter (only once an admin has enabled per-service fields) -->
    <div v-if="customFieldsEnabled && !isTeamLeader && !(currentUser && currentUser.role === 'contractor')"
      class="w-full flex items-center gap-2 px-3 py-2">
      <label class="text-xs font-medium text-gray-500">Service</label>
      <select :value="selectedService" @change="onServiceChange($event.target.value)"
        class="text-xs h-8 px-2 rounded border border-gray-200 bg-white outline-none cursor-pointer">
        <option value="all">All Services</option>
        <option v-for="s in customServices" :key="s.key" :value="s.key">{{ s.label }}</option>
      </select>
    </div>

    <div class="w-full overflow-x-auto" ref="scrollBody" style="min-height: 70vh"
      :class="(currentUser && currentUser.role === 'contractor') || isTeamLeader ? 'hidden' : ''">
      <table class="bg-gray-100 border-collapse" style="table-layout: fixed">
        <thead @mouseout="columnHover = -1">
          <tr>
            <th v-for="(h, hIndex) in allVisibleColumns" :key="'col' + h._origIndex"
              @mouseover="columnHover = h._origIndex"
              :class="[
                currentlySortedBy == h._origIndex
                  ? 'bg-primary text-white font-bold'
                  : 'bg-secondary text-gray-400',
              ]"
              :style="{ width: (colWidthMap[h.field] || 100) + 'px' }"
              class="sticky top-0 z-10 relative border-r border-gray-600 cursor-pointer hover:bg-gray-900 font-normal h-12 text-xs whitespace-nowrap px-3">
              <div @click="sortCol(h._origIndex)" class="w-full h-full flex items-center justify-center space-x-1 overflow-hidden">
                <span class="capitalize truncate">{{ columnLabel(h.field) }}</span>
                <i @click.stop="openFilterDropdown($event, h)" class="fa-solid fa-filter text-xs opacity-60" v-if="h.filter"></i>
              </div>
            </th>
            <!-- Action column headers -->
            <th v-if="currentUser.role == 'admin'" class="sticky top-0 z-10 bg-secondary text-gray-400 border-r border-gray-600 font-normal h-12 text-xs px-3 whitespace-nowrap" style="min-width:150px">
              Notes
            </th>
            <th v-if="currentUser.role == 'admin'" class="sticky top-0 z-10 bg-secondary text-gray-400 border-r border-gray-600 font-normal h-12 text-xs px-2 whitespace-nowrap" style="min-width:100px">
              Client SMS
            </th>
            <th v-if="currentUser.role == 'admin'" @click="showPublishAll = true"
              class="sticky top-0 z-10 bg-primary cursor-pointer hover:bg-blue-700 text-white font-normal h-12 text-xs px-3 whitespace-nowrap" style="min-width:100px">
              Publish
            </th>
            <th v-if="currentUser.role == 'contractor'" class="sticky top-0 z-10 bg-secondary text-gray-300 font-normal h-12 text-xs px-3" style="min-width:180px">
              {{ contractorTab === 'completed' ? 'Status' : 'Actions' }}
            </th>
            <th @click="openColumnSettings()"
              class="sticky top-0 z-10 bg-secondary text-gray-400 cursor-pointer hover:bg-gray-700 font-normal h-12 text-xs px-3 whitespace-nowrap" style="min-width:40px">
              <i class="fa-solid fa-gear"></i>
            </th>
          </tr>
        </thead>
        <tbody>
          <template v-for="(i, index) in (currentUser && currentUser.role === 'contractor' ? contractorFilteredRows : rowData)">
            <tr v-if="i.visible && rowMatchesService(i) && nowLineAfterIndex === 'before-first-today' && index === firstTodayRowIndex" :key="'now-line-before-' + index" class="now-time-row">
              <td :colspan="allVisibleColumns.length + 4" class="now-time-cell"><div class="now-time-line"></div></td>
            </tr>
            <tr v-if="i.visible && rowMatchesService(i)" :key="'row-' + index"
              :data-job-id="i.id"
              :class="[
                i.status == 'ON-CALL' ? 'bg-yellow-50' : 'bg-white',
                i.status == 'COMPLETED' ? 'bg-green-200' : '',
                i.status == 'CANCELLED' ? 'bg-red-200' : '',
                currentUser && currentUser.role === 'contractor' && contractorTab === 'completed' ? 'opacity-75' : '',
              ]"
              class="border-b border-gray-200 hover:bg-blue-50 transition-colors">
              <td v-for="(b, bIndex) in allVisibleColumns" :key="'bIndex' + b._origIndex"
                :class="[columnHover == b._origIndex ? 'bg-black bg-opacity-5' : '']"
                :style="{ width: (colWidthMap[b.field] || 100) + 'px', maxWidth: (colWidthMap[b.field] || 100) + 'px' }"
                class="px-3 h-12 overflow-hidden">
                <!-- Time (flatpickr) -->
                <flat-pickr v-if="b.field == 'time'"
                  class="h-10 cursor-pointer w-16 text-xs text-gray-600 text-center bg-gray-100 rounded" :config="config"
                  v-model="i.time" @on-close="handleChange(i, i.time, i.id, $event)"></flat-pickr>
                <!-- Team Leader (admin: multi-select, others: read-only) -->
                <div v-else-if="b.field === 'teamLeader'"
                  :class="currentUser.role === 'admin' ? 'cursor-pointer hover:bg-black hover:bg-opacity-5 rounded' : ''"
                  class="truncate text-xs text-gray-700"
                  @click.stop="currentUser.role === 'admin' && openTeamLeaderPicker($event, i)">
                  <span v-if="i.teamLeader">{{ i.teamLeader }}</span>
                  <span v-else class="text-gray-300">—</span>
                  <i v-if="currentUser.role === 'admin'" class="fa-solid fa-chevron-down ml-1 text-gray-400" style="font-size:8px"></i>
                </div>
                <!-- Tester -->
                <div v-else-if="b.field === 'tester'" class="cursor-pointer" @click="showJobEdit('tester', i)">
                  <div class="flex flex-col items-center gap-1">
                    <span :class="testerStyle(i)" class="text-xs truncate block w-full">
                      {{ i.tester || 'No Tester' }}
                    </span>
                    <div v-if="i.tester"
                      @click.stop="openTesterSmsModal(i)"
                      class="h-5 px-2 bg-primary hover:bg-secondary text-white rounded flex items-center justify-center cursor-pointer" style="font-size: 9px">
                      SMS
                    </div>
                  </div>
                </div>
                <!-- Accepted -->
                <div v-else-if="b.field === 'accepted'" class="text-center">
                  <span :class="[
                    i.rejected
                      ? 'bg-yellow-500'
                      : i.accepted
                        ? 'bg-green-500'
                        : 'bg-red-600'
                  ]" class="text-white rounded py-1 px-2 text-xs whitespace-nowrap">
                    {{ i.rejected ? 'REJECTED' : i.accepted ? 'YES' : 'NO' }}
                  </span>
                </div>
                <!-- Status -->
                <div v-else-if="b.field === 'status'">
                  <div :class="jobStyles(i)" class="rounded px-1">
                    <select @change="updateStatus(i)" v-model="i.status"
                      class="text-xs outline-none bg-transparent text-center w-full h-10 cursor-pointer">
                      <option>FIRM</option>
                      <option>ON-CALL</option>
                      <option>COMMITTED</option>
                      <option>COMPLETED</option>
                      <option>CANCELLED</option>
                    </select>
                  </div>
                </div>
                <!-- Generic data cell -->
                <div v-else @click="showJobEdit(b.field, i)" class="truncate text-xs text-gray-700 cursor-pointer">
                  {{ i[b.field] }}
                </div>
              </td>
              <!-- Notes -->
              <td class="px-1 h-12" v-if="currentUser.role == 'admin'">
                <input type="text" @keyup="updateNote(i)" v-model="i.notes"
                  class="w-36 h-10 bg-gray-100 rounded px-2 text-xs outline-none focus:ring-1 focus:ring-primary" placeholder="Add note..." />
              </td>
              <!-- Client SMS -->
              <td class="px-2 h-12 text-center" v-if="currentUser.role == 'admin'">
                <div class="flex items-center justify-center space-x-1">
                  <i v-if="i.clientSmsStatus === 'FIRM'" class="fa-solid fa-circle-check text-green-500 cursor-pointer" title="FIRM via SMS – click to resolve" @click="openChangeResolveModal(i)"></i>
                  <i v-else-if="i.clientSmsStatus === 'CHANGED'" class="fa-solid fa-circle-question text-yellow-500 cursor-pointer" title="CHANGED via SMS – click to resolve" @click="openChangeResolveModal(i)"></i>
                  <i v-else-if="i.clientSmsStatus === 'CANCELLED'" class="fa-solid fa-circle-xmark text-red-500 cursor-pointer" title="CANCELLED via SMS – click to resolve" @click="openChangeResolveModal(i)"></i>
                  <div @click="openClientSmsModal(i)"
                    class="h-7 w-7 bg-indigo-100 hover:bg-indigo-200 text-indigo-600 rounded flex items-center justify-center cursor-pointer">
                    <i class="fa-solid fa-comment-sms text-xs"></i>
                  </div>
                  <div @click="openLinkModal(i)"
                    class="h-7 w-7 bg-green-100 hover:bg-green-200 text-green-600 rounded flex items-center justify-center cursor-pointer"
                    title="Client Link">
                    <i class="fa-solid fa-link text-xs"></i>
                  </div>
                </div>
              </td>
              <!-- Publish -->
              <td class="px-1 h-12" v-if="currentUser.role == 'admin'">
                <select @change="updateJob(rowData.indexOf(i))" v-model="i.published" :class="i.published
                    ? 'bg-green-100 text-green-700 border-green-200'
                    : 'bg-red-50 text-red-600 border-red-200'
                  " class="text-xs outline-none text-center w-full h-10 rounded border cursor-pointer">
                  <option :value="true">Published</option>
                  <option :value="false">Unpublished</option>
                </select>
              </td>
              <!-- Contractor sign off / en-route -->
              <td v-if="currentUser.role == 'contractor'" class="px-2 h-12 whitespace-nowrap">
                <!-- Completed tab: show status badge -->
                <template v-if="contractorTab === 'completed'">
                  <span v-if="i.status === 'COMPLETED'" class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-green-500 text-white">
                    <i class="fa-solid fa-circle-check mr-1"></i>COMPLETED
                  </span>
                  <span v-else-if="i.status === 'CANCELLED'" class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-red-500 text-white">
                    <i class="fa-solid fa-circle-xmark mr-1"></i>CANCELLED
                  </span>
                </template>
                <!-- Current tab: show en-route + sign off buttons -->
                <template v-else>
                  <div class="flex items-center gap-1.5">
                    <button v-if="!i.enRoute" @click.stop="enRouteJob = i; enRouteModal = true"
                      class="text-xs font-bold px-3 h-8 bg-yellow-500 text-white rounded hover:bg-yellow-600 transition-colors whitespace-nowrap">
                      En-Route
                    </button>
                    <span v-else class="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-700">
                      <i class="fa-solid fa-route mr-1"></i>En-Route
                    </span>
                    <button @click.stop="signOff = rowData.indexOf(i)"
                      class="text-xs font-bold px-3 h-8 bg-gray-800 text-white rounded hover:bg-gray-900 transition-colors whitespace-nowrap">
                      Sign Off
                    </button>
                  </div>
                </template>
              </td>
              <!-- Spacer for settings cog -->
              <td class="h-12 px-3"></td>
            </tr>
            <tr v-if="i.visible && nowLineAfterIndex === index" :key="'now-line-' + index" class="now-time-row">
              <td :colspan="allVisibleColumns.length + 4" class="now-time-cell"><div class="now-time-line"></div></td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <div v-if="scheduleLoading" class="w-full py-40 flex flex-col items-center justify-center">
      <i class="fa-solid fa-spinner fa-spin text-3xl text-indigo-500 mb-4"></i>
      <p class="text-gray-400 text-sm">Loading schedule...</p>
    </div>
    <div v-else-if="rowData.length < 1" class="text-gray-400 text-lg w-full py-40 flex justify-center">
      Sorry, no jobs found on this date
    </div>

    <!-- Filter Dropdown (fixed position, outside overflow container) -->
    <div v-if="activeFilterCol" class="fixed inset-0" style="z-index: 9999" @click.self="closeFilterDropdown()">
      <div class="fixed p-4 w-56 bg-white rounded-xl shadow-xl border border-gray-200 flex flex-col"
        :style="{ top: filterDropdownPos.top + 'px', left: filterDropdownPos.left + 'px', maxHeight: filterDropdownPos.maxHeight + 'px' }">
        <div class="w-full text-left text-gray-400 relative flex flex-col flex-1 min-h-0">
          <div class="absolute top-0 right-0 hover:text-primary cursor-pointer" @click="closeFilterDropdown()">
            x
          </div>
          <h2 class="text-secondary text-sm flex-shrink-0">Filter by {{ activeFilterCol.filter }}</h2>
          <div class="w-full flex flex-col mt-2 flex-1 min-h-0" v-if="activeFilterCol.filter == 'select'">
            <div class="overflow-y-auto flex-1 min-h-0">
              <div class="w-full flex flex-wrap space-x-2 cursor-pointer" v-for="(s, sIndex) in activeFilterCol.filterValueArray"
                :key="'selectFilter' + sIndex">
                <div class="flex-shrink order-1">
                  <input @change="filterColumn($event, s, activeFilterCol._origIndex)" v-model="s.visible" type="checkbox"
                    class="cursor-pointer" name="select" :id="'filterOpt' + sIndex" />
                </div>
                <div class="flex-grow order-2">
                  <label :for="'filterOpt' + sIndex" class="cursor-pointer">{{ s.value }}</label>
                </div>
              </div>
            </div>
            <div @click="closeFilterDropdown()" class="w-full mt-1 text-right text-xs text-primary cursor-pointer flex-shrink-0">
              Close
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Team Leader multi-select (admin only) -->
    <div v-if="teamLeaderPickerJob" class="fixed inset-0" style="z-index: 9999" @click.self="teamLeaderPickerJob = null">
      <div class="fixed p-4 w-64 bg-white rounded-xl shadow-xl border border-gray-200 flex flex-col"
        :style="{ top: teamLeaderPickerPos.top + 'px', left: teamLeaderPickerPos.left + 'px', maxHeight: teamLeaderPickerPos.maxHeight + 'px' }">
        <div class="flex items-center flex-shrink-0">
          <h2 class="text-secondary text-sm font-bold">Team Leaders</h2>
          <i @click="teamLeaderPickerJob = null" class="fa-solid fa-xmark ml-auto text-gray-400 hover:text-gray-600 cursor-pointer"></i>
        </div>
        <div v-if="allTeamLeaders.length === 0" class="text-xs text-gray-400 py-3 text-center">No team leaders found</div>
        <div v-else class="overflow-y-auto mt-2 flex-1 min-h-0">
          <label v-for="tl in allTeamLeaders" :key="tl.id"
            class="w-full flex items-center py-1.5 cursor-pointer text-sm text-gray-700 hover:text-primary">
            <input type="checkbox" class="cursor-pointer mr-2"
              :checked="(teamLeaderPickerJob.teamLeaderIds || []).includes(tl.id)"
              @change="toggleTeamLeaderOnJob(teamLeaderPickerJob, tl)" />
            <i class="fa-solid fa-star text-yellow-500 mr-1.5" style="font-size:9px"></i>
            <span class="truncate">{{ tl.name }}</span>
            <span v-if="tl.region" class="text-xs text-gray-400 ml-1 truncate">({{ tl.region }})</span>
          </label>
        </div>
      </div>
    </div>

    <!-- Map Modal -->
    <div v-if="showMapModal" class="w-full z-50 h-screen fixed top-0 left-0 bg-gray-900 bg-opacity-60 flex items-center justify-center" style="backdrop-filter:blur(2px)">
      <div class="bg-white rounded-xl shadow-2xl w-full max-w-5xl mx-4 flex flex-col" style="height:85vh">
        <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <h3 class="text-lg font-bold text-gray-900"><i class="fa-solid fa-map-location-dot text-indigo-600 mr-2"></i>Job Locations <span class="text-sm font-normal text-gray-400 ml-2">({{ visibleRows.length }} jobs)</span></h3>
          <button @click="closeMapModal()" class="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-gray-600">
            <i class="fa-solid fa-xmark text-lg"></i>
          </button>
        </div>
        <div class="flex-1 relative">
          <div id="schedule-map" class="w-full h-full rounded-b-xl"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  getDocs,
} from "firebase/firestore";
import { postFire, setFire, putFire, getFire, getTodaysTimesheet, getTeamMembers, getTeamLeaders, createTeamLeaderInvite } from "../../firestore/utils";
import { getTimeStamp, db, auth } from "../../firebase";
import { onAuthStateChanged } from "firebase/auth";

import { bus } from "../../main";

import { Parser } from "json2csv";

import { Datetime } from "vue-datetime";
import "vue-datetime/dist/vue-datetime.css";

import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";

import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

import {
  DEFAULT_SERVICE,
  activeServices,
  allFieldDefs,
  allFieldKeys,
  applyCustomFieldTokens,
  fieldsFor,
  loadDefinitions,
  resolveFieldValues,
  resolveService,
  smsShortcodesForJob,
  templatesForJob,
} from "../../services/customFields";

const MAPBOX_TOKEN = "pk.eyJ1IjoicHJlbWFya2V0aG9tZXMiLCJhIjoiY21qamRkb3BsMGU2NzNlb3k4MWs2Y3NtaCJ9.JxBalubMXzSxJWsc2eTWrw";

let unsubscribe;
let notifUnsubscribe;

export default {
  props: ["date", "search", "triggerExport"],
  data() {
    return {
      scheduleLoading: true,
      selectedCustomerId: null,
      config: {
        enableTime: true,
        altFormat: "G:iK",
        altInput: true,
        allowInput: false,
      },
      showNotificationPopup: [],
      showTesters: false,
      selectedJob: null,
      testers: [],
      smsTester: null,
      smsModal: false,
      smsBox: "",
      clientSmsModal: false,
      clientSmsJob: null,
      // Job the currently loaded template list is scoped to. Null for bulk.
      smsTemplateJob: null,
      clientSmsPhone: '',
      clientSmsMessage: '',
      clientSmsHistory: [],
      clientSmsLoading: false,
      clientSmsRecipient: 'primary',
      clientSmsManualPhone: '',
      clientSmsJobDirty: false,
      clientSmsJobSaving: false,
      clientSmsJobSaved: false,
      clientSmsDateConfig: {
        enableTime: true,
        altFormat: "d-m-Y H:i",
        altInput: true,
        allowInput: false,
      },
      linkModal: false,
      linkJob: null,
      linkUrl: '',
      linkCopied: false,
      clientAlert: null,
      changeResolveModal: false,
      changeResolveJob: null,
      smsTemplates: [],
      selectedSmsTemplateId: '',
      initialLoadComplete: false,
      showColumnSettings: false,
      columnConfig: [],
      modalColumnConfig: [],
      nowTime: new Date(),
      nowTimeTimer: null,
      showStartTimes: false,
      startTimeEntries: [],
      timesheetStartTime: null,
      // Bulk SMS
      bulkSmsRecipientType: 'primary',
      bulkSmsModal: false,
      bulkSmsMessage: '',
      bulkSmsRecipients: [],
      bulkSmsGroupByContact: true,
      bulkSmsSending: false,
      bulkSmsSent: 0,
      bulkSmsTotal: 0,
      bulkSmsResults: [],
      bulkSmsDone: false,
      signOff: -1,
      contractorTab: 'current',
      teamMembers: [],
      allTeamLeaders: [],
      teamLeaderPickerJob: null,
      teamLeaderPickerPos: { top: 0, left: 0, maxHeight: 400 },
      inviteModalJob: null,
      pendingInvites: [],
      currentInvite: null,
      inviteUnsubscribe: null,
      nextJobModal: false,
      nextJob: null,
      enRouteModal: false,
      enRouteJob: null,
      enRouteSending: false,
      showPublishAll: false,
      currentlySortedBy: null,
      columnHover: -1,
      activeFilterField: null,
      filterDropdownPos: { top: 0, left: 0, maxHeight: 400 },
      columnDefs: [
        {
          field: "date",
          width: "w-16",
          filterField: "date",
          openFilter: false,
          filterValue: "",
          filterValueArray: [],
          sortDirection: "asc",
        },
        {
          field: "time",
          width: "w-16",
          filterField: "time",
          openFilter: false,
          filterValue: "",
          filterValueArray: [],
          sortDirection: "asc",
        },
        // {
        //   field: "poNumber",
        //   width: "w-20",
        //   filterField: "poNumber",
        //   openFilter: false,
        //   filterValue: "",
        //   filterValueArray: [],
        //   sortDirection: "asc",
        // },
        {
          field: "client",
          width: "w-10",
          filterField: "client",
          openFilter: false,
          filterValue: "",
          filterValueArray: [],
          sortDirection: "asc",
        },
        // {
        //   field: "projectId",
        //   width: "w-12",
        //   filterField: "projectId",
        //   filter: "",
        //   openFilter: false,
        //   filterValue: "",
        //   filterValueArray: [],
        //   sortDirection: "asc",
        // },
        {
          field: "projectName",
          width: "w-32",
          filterField: "projectName",
          filter: "",
          openFilter: false,
          filterValue: "",
          filterValueArray: [],
          sortDirection: "asc",
        },
        {
          field: "customer",
          width: "w-20",
          filterField: "company",
          filter: "select",
          openFilter: false,
          filterValue: "",
          filterValueArray: [],
          sortDirection: "asc",
        },
        {
          field: "region",
          width: "w-20",
          filterField: "region",
          filter: "select",
          openFilter: false,
          filterValue: "",
          filterValueArray: [],
          sortDirection: "asc",
        },
        {
          field: "fullAddress",
          filterField: "fullAddress",
          width: "w-56",
          openFilter: false,
          filterValue: "",
          filterValueArray: [],
          sortDirection: "asc",
        },
        // {
        //   field: "suburb",
        //   filterField: "address.locality",
        //   width: "w-32",
        //   filter: "select",
        //   openFilter: false,
        //   filterValue: "",
        //   filterValueArray: [],
        //   sortDirection: "asc",
        // },
        {
          field: "mixCode",
          width: "w-10",
          filterField: "mixCode",
          openFilter: false,
          filterValue: "",
          filterValueArray: [],
          sortDirection: "asc",
        },
        {
          field: "meters",
          width: "w-14",
          filterField: "meters",
          openFilter: false,
          filterValue: "",
          filterValueArray: [],
          sortDirection: "asc",
        },
        {
          field: "duration",
          width: "w-14",
          filterField: "duration",
          openFilter: false,
          filterValue: "",
          filterValueArray: [],
          sortDirection: "asc",
        },

        {
          field: "requirements",
          filterField: "requirements",
          openFilter: false,
          width: "w-32",
          filterValue: "",
          filterValueArray: [],
          sortDirection: "asc",
        },
        {
          field: "teamLeader",
          filterField: "teamLeader",
          width: "w-20",
          filter: "select",
          openFilter: false,
          filterValue: "",
          filterValueArray: [],
          sortDirection: "asc",
        },
        {
          field: "tester",
          filterField: "contractor.name",
          width: "w-20",
          filter: "select",
          openFilter: false,
          filterValue: "",
          filterValueArray: [],
          sortDirection: "asc",
        },
        {
          field: "accepted",
          filterField: "accepted",
          width: "w-10",
          filter: "select",
          openFilter: false,
          filterValue: "",
          filterValueArray: [],
          sortDirection: "asc",
        },
        {
          field: "status",
          filterField: "status",
          width: "w-28",
          filter: "select",
          openFilter: false,
          filterValue: "",
          filterValueArray: [],
          sortDirection: "asc",
        },
      ],
      rowData: [],
      showMapModal: false,
      scheduleMap: null,
      scheduleMapMarkers: [],

      // Per-service custom fields
      customFieldDefs: null,
      selectedService: "all",
      // The columnDefs fields that ship with the app, captured before any
      // custom columns are spliced in so rebuilds know what to remove.
      baseColumnFields: [],
    };
  },
  computed: {
    customFieldsEnabled() {
      return !!(this.customFieldDefs && this.customFieldDefs.enabled);
    },
    customServices() {
      return activeServices(this.customFieldDefs);
    },
    inviteFields() {
      return (this.currentInvite && this.currentInvite.customFields) || [];
    },
    serviceFilterActive() {
      return this.customFieldsEnabled && this.selectedService !== "all";
    },
    // Feeds the map modal, CSV export, Publish All and bulk SMS, so it has to
    // respect the service filter — otherwise Publish All would notify testers
    // across every service.
    visibleRows() {
      return this.rowData.filter((i) => i.visible && this.rowMatchesService(i));
    },
    contractorFilteredRows() {
      if (!this.currentUser || this.currentUser.role !== 'contractor') return this.rowData;
      return this.rowData.filter((i) => {
        if (this.contractorTab === 'current') {
          return i.status !== 'COMPLETED' && i.status !== 'CANCELLED';
        } else {
          return i.status === 'COMPLETED' || i.status === 'CANCELLED';
        }
      });
    },
    currentUser() {
      return this.$store.state.currentUser;
    },
    isTeamLeader() {
      return this.currentUser && this.currentUser.isTeamLeader && this.currentUser.role !== 'admin';
    },
    teamLeaderFilteredRows() {
      if (!this.isTeamLeader) return [];
      return this.rowData.filter(r => r.visible);
    },
    availableTeamLeadersForInvite() {
      if (!this.inviteModalJob) return [];
      const assignedIds = this.inviteModalJob.teamLeaderIds || [];
      return this.allTeamLeaders.filter(tl => !assignedIds.includes(tl.id));
    },
    nowLineAfterIndex() {
      const now = this.nowTime;
      const todayStr = this.$moment(now).format('DD MMM');

      // Collect visible rows that are today (date column shows "DD MMM")
      const todayRows = [];
      for (let idx = 0; idx < this.rowData.length; idx++) {
        const row = this.rowData[idx];
        if (row.visible && row.date === todayStr) {
          todayRows.push(idx);
        }
      }
      if (todayRows.length === 0) return -1;

      // Compare only hour/minute against now for today's rows
      const nowMinutes = now.getHours() * 60 + now.getMinutes();
      let lastPastIndex = -1;
      for (const idx of todayRows) {
        const raw = this.rowData[idx].time;
        const t = raw instanceof Date ? raw : new Date(raw);
        if (isNaN(t.getTime())) continue;
        const rowMinutes = t.getHours() * 60 + t.getMinutes();
        if (rowMinutes <= nowMinutes) {
          lastPastIndex = idx;
        }
      }

      if (lastPastIndex === -1) {
        // All today's rows are in the future — line before first today row
        return 'before-first-today';
      }
      return lastPastIndex;
    },
    firstTodayRowIndex() {
      const todayStr = this.$moment(this.nowTime).format('DD MMM');
      return this.rowData.findIndex(r => r.visible && this.rowMatchesService(r) && r.date === todayStr);
    },
    allVisibleColumns() {
      return this.columnDefs
        .map((col, idx) => {
          col._origIndex = idx;
          return col;
        })
        .filter(col => {
          const cfg = this.columnConfig.find(c => c.field === col.field);
          return cfg ? cfg.visible : true;
        });
    },
    colWidthMap() {
      const sizeMap = this.colSizeWidths();
      const map = {};
      this.columnConfig.forEach(cfg => {
        const size = cfg.size || 'M';
        const fieldSizes = sizeMap[cfg.field] || { S: 60, M: 100, L: 160 };
        map[cfg.field] = fieldSizes[size] || fieldSizes['M'];
      });
      return map;
    },
    // Lookup of which team leader table columns are switched on
    tlColShown() {
      const shown = {};
      this.teamLeaderColumnDefs().forEach(col => {
        const cfg = this.columnConfig.find(c => c.field === col.field);
        shown[col.field] = cfg ? cfg.visible : true;
      });
      return shown;
    },
    teamLeaderColWidthMap() {
      const sizeMap = this.teamLeaderColSizeWidths();
      const map = {};
      this.teamLeaderColumnDefs().forEach(col => {
        const cfg = this.columnConfig.find(c => c.field === col.field);
        const sizes = sizeMap[col.field] || { S: 80, M: 120, L: 180 };
        map[col.field] = sizes[(cfg && cfg.size) || 'M'] || sizes.M;
      });
      return map;
    },
    activeFilterCol() {
      if (this.activeFilterField === null) return null;
      return this.columnDefs.find(c => c.field === this.activeFilterField) || null;
    },
    contractorStartTime() {
      if (!this.currentUser || this.currentUser.role !== 'contractor') return null;
      // Timesheet takes priority (set from admin Start Times modal)
      if (this.timesheetStartTime) return this.timesheetStartTime;
      // Fallback to testerStartTime on job documents
      const job = this.rowData.find(j => j.testerStartTime);
      return job ? job.testerStartTime : null;
    },
    clientSmsShortcodes() {
      return [
        { code: '{recipientName}', label: 'Recipient name' },
        { code: '{company}', label: 'Company' },
        { code: '{project}', label: 'Project name' },
        { code: '{contact}', label: 'Primary contact' },
        { code: '{address}', label: 'Job address' },
        { code: '{date}', label: 'Job date' },
        { code: '{time}', label: 'Job time' },
        { code: '{meters}', label: 'Meters' },
        { code: '{link}', label: 'Client portal link' },
      ].concat(smsShortcodesForJob(this.customFieldDefs, this.clientSmsJob));
    },
    // Service-locked templates only appear for a job of that service, and never
    // on bulk sends where the services are mixed.
    visibleSmsTemplates() {
      return templatesForJob(this.customFieldDefs, this.smsTemplates, this.smsTemplateJob);
    },
    bulkSmsShortcodes() {
      return [
        { code: '{recipientName}', label: 'Recipient name' },
        { code: '{company}', label: 'Company' },
        { code: '{project}', label: 'Project name' },
        { code: '{contact}', label: 'Primary contact' },
        { code: '{address}', label: 'Job address' },
        { code: '{date}', label: 'Job date' },
        { code: '{time}', label: 'Job time' },
        { code: '{meters}', label: 'Meters' },
        { code: '{link}', label: 'Client portal link' },
      ];
    },
    bulkSmsSelectedCount() {
      return this.bulkSmsRecipients.filter(r => r.selected).length;
    },
    bulkSmsProgress() {
      if (!this.bulkSmsTotal) return 0;
      return Math.round((this.bulkSmsSent / this.bulkSmsTotal) * 100);
    },
    bulkSmsSuccessCount() {
      return this.bulkSmsResults.filter(r => r.success).length;
    },
    bulkSmsFailedCount() {
      return this.bulkSmsResults.filter(r => !r.success).length;
    },
  },
  watch: {
    currentUser(newVal, oldVal) {
      if (newVal && (!oldVal || newVal.id !== (oldVal && oldVal.id))) {
        this.initColumnConfig();
      }
    },
    triggerExport() {
      if (this.triggerExport) this.exportToCSV();
    },
    columnDefs: {
      handler() {
        // this.filterResults();
      },
      deep: true,
    },
    search() {
      this.filterResults();
    },
    date: {
      immediate: false,
      handler(newVal, oldVal) {
        this.scheduleLoading = true;
        this.initialLoadComplete = false;
        this.getItems();
      },
    },
    // prevDate: {
    //   immediate: false,
    //   handler(newVal, oldVal) {
    //     // alert(this.date)
    //     this.getItems();
    //   },
    // },
    rowData: {
      handler() {
        bus.$emit("jobs", this.rowData);
      },
      deep: true,
    },
  },
  methods: {
    // ==================== MAP MODAL ====================

    getJobCoords(item) {
      if (!item) return null;
      if (item.geopoint) {
        const gp = item.geopoint;
        if (gp.latitude && gp.longitude) return { lat: gp.latitude, lng: gp.longitude };
        if (gp._lat && gp._long) return { lat: gp._lat, lng: gp._long };
      }
      if (item.address && typeof item.address === 'object') {
        if (item.address.latitude && item.address.longitude) return { lat: item.address.latitude, lng: item.address.longitude };
      }
      if (item.latitude && item.longitude) return { lat: item.latitude, lng: item.longitude };
      return null;
    },

    openMapModal() {
      this.showMapModal = true;
      this.$nextTick(() => this.initScheduleMap());
    },

    closeMapModal() {
      this.showMapModal = false;
      this.destroyScheduleMap();
    },

    initScheduleMap() {
      const container = document.getElementById('schedule-map');
      if (!container) return;

      this.destroyScheduleMap();

      mapboxgl.accessToken = MAPBOX_TOKEN;
      this.scheduleMap = new mapboxgl.Map({
        container: 'schedule-map',
        style: 'mapbox://styles/mapbox/dark-v11',
        center: [151.2093, -33.8688],
        zoom: 10,
      });
      this.scheduleMap.addControl(new mapboxgl.NavigationControl(), 'top-right');

      this.scheduleMap.on('load', () => {
        const jobs = this.visibleRows;
        const bounds = new mapboxgl.LngLatBounds();
        let hasPoints = false;

        jobs.forEach((job) => {
          const coords = this.getJobCoords(job);
          if (!coords) return;

          bounds.extend([coords.lng, coords.lat]);
          hasPoints = true;

          const timeStr = job.time ? this.$moment(job.time).format('HH:mm') : '';
          const dateStr = job.date || '';
          const address = job.fullAddress || job.formattedAddress || '';
          const status = job.status || '';
          const tester = job.tester || 'Unassigned';
          const company = job.company || '';
          const mix = job.mixCode || '';
          const meters = job.meters ? `${job.meters}m³` : '';

          // Marker colour based on status
          let color = '#6366f1';
          if (status === 'FIRM' || status === 'COMMITTED') color = '#22c55e';
          else if (status === 'ON-CALL') color = '#eab308';
          else if (status === 'CANCELLED') color = '#ef4444';

          const el = document.createElement('div');
          el.style.cssText = `width:36px;height:36px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:bold;border:3px solid ${color};background:#1a1a2e;color:${color};cursor:pointer;box-shadow:0 0 10px ${color}88;`;
          el.textContent = timeStr || '?';

          const popupHtml = `<div style="font-size:12px;padding:6px;min-width:180px;">
            <div style="font-weight:bold;margin-bottom:4px;">${timeStr} — ${dateStr}</div>
            <div style="color:#888;margin-bottom:3px;">${company}</div>
            <div style="color:#666;margin-bottom:3px;">${address}</div>
            <div style="margin-bottom:2px;"><span style="font-weight:600;">Status:</span> ${status}</div>
            <div style="margin-bottom:2px;"><span style="font-weight:600;">Tester:</span> ${tester}</div>
            ${mix || meters ? `<div>${[mix, meters].filter(Boolean).join(' — ')}</div>` : ''}
          </div>`;

          const marker = new mapboxgl.Marker({ element: el })
            .setLngLat([coords.lng, coords.lat])
            .setPopup(new mapboxgl.Popup({ offset: 10 }).setHTML(popupHtml))
            .addTo(this.scheduleMap);
          this.scheduleMapMarkers.push(marker);
        });

        if (hasPoints) {
          if (this.scheduleMapMarkers.length === 1) {
            const lngLat = this.scheduleMapMarkers[0].getLngLat();
            this.scheduleMap.flyTo({ center: [lngLat.lng, lngLat.lat], zoom: 14 });
          } else {
            this.scheduleMap.fitBounds(bounds, { padding: 60, maxZoom: 15 });
          }
        }
      });
    },

    destroyScheduleMap() {
      this.scheduleMapMarkers.forEach(m => m.remove());
      this.scheduleMapMarkers = [];
      if (this.scheduleMap) {
        this.scheduleMap.remove();
        this.scheduleMap = null;
      }
    },

    teamLeaderNames(row) {
      return (row.teamLeaders || []).map((t) => t.name).filter((n) => n);
    },
    colDef(field) {
      const idx = this.columnDefs.findIndex(c => c.field === field);
      if (idx === -1) return null;
      // filterColumn() looks the column up by _origIndex, which is normally
      // assigned by the allVisibleColumns computed used by the admin table.
      this.columnDefs[idx]._origIndex = idx;
      return this.columnDefs[idx];
    },
    // Anchors a fixed-position popover to an element, flipping it above the
    // anchor when it would otherwise run off the bottom of the viewport.
    anchorPopover(rect, width) {
      const margin = 8;
      const gap = 4;
      const below = window.innerHeight - rect.bottom - margin - gap;
      const above = rect.top - margin - gap;
      const openUp = below < 220 && above > below;
      const maxHeight = Math.max(140, Math.min(400, openUp ? above : below));
      return {
        top: openUp ? Math.max(margin, rect.top - gap - maxHeight) : rect.bottom + gap,
        left: Math.max(margin, Math.min(rect.left, window.innerWidth - width - margin)),
        maxHeight,
      };
    },
    openFilterDropdown(event, h) {
      if (this.activeFilterField === h.field) {
        this.activeFilterField = null;
        return;
      }
      this.filterDropdownPos = this.anchorPopover(event.target.getBoundingClientRect(), 224);
      this.activeFilterField = h.field;
    },
    closeFilterDropdown() {
      this.activeFilterField = null;
    },
    startNotificationsListener() {
      if (notifUnsubscribe) {
        notifUnsubscribe();
        notifUnsubscribe = null;
      }
      const fiveMinAgo = new Date(Date.now() - 5 * 60 * 1000);
      const q = query(
        collection(db, "notifications"),
        where("type", "in", ["job_cancelled", "job_changed"]),
        where("created", ">", fiveMinAgo),
        orderBy("created", "desc")
      );
      let initialLoad = true;
      notifUnsubscribe = onSnapshot(q, (snapshot) => {
        if (initialLoad) {
          initialLoad = false;
          return;
        }
        snapshot.docChanges().forEach((change) => {
          if (change.type === "added") {
            const data = change.doc.data();
            const created = data.created ? (data.created.seconds ? new Date(data.created.seconds * 1000) : new Date(data.created)) : new Date();
            const notification = {
              status: data.type === "job_cancelled" ? "CANCELLED" : "CHANGED",
              company: data.company || "",
              projectName: data.projectName || "",
              date: "",
              address: "",
              message: data.body || "",
              jobId: data.jobId || "",
              timestamp: created.getTime(),
              read: false,
            };
            this.$store.commit("addClientNotification", notification);
            this.clientAlert = notification;
          }
        });
      });
    },
    filterColumn(event, col, hIndex) {
      const columnName = col.value;
      let arr = [];
      const obj = this.columnDefs[hIndex].filterValueArray;
      // Loop through the columns unique values using param filterValueArray
      for (let i = 0; i < obj.length; i++) {
        // any object that is NOT selected push to an array
        if (!obj[i].visible) arr.push(obj[i].value);
      }

      // If we have nothing selected (all unchecked) → show all rows
      let clear = false;
      if (arr.length == this.columnDefs[hIndex].filterValueArray.length)
        clear = true;

      // Build set of selected (visible) values for quick lookup
      const selectedValues = new Set();
      for (let i = 0; i < obj.length; i++) {
        if (obj[i].visible) selectedValues.add(obj[i].value);
      }

      // Now loop through all rowData and hide any rows where arr includes the rowData['columnName']
      const field = this.columnDefs[hIndex].field;
      for (let i = 0; i < this.rowData.length; i++) {
        if (clear) {
          this.rowData[i].visible = true;
        } else if (field === "teamLeader") {
          // A job can carry several leaders — show it if any of them is selected
          const names = this.teamLeaderNames(this.rowData[i]);
          this.rowData[i].visible = names.length
            ? names.some((n) => selectedValues.has(n))
            : selectedValues.has("Unassigned");
        } else {
          const val = this.rowData[i][field];
          // For blank/empty values, show them if "Unassigned" is selected
          if (!val) {
            this.rowData[i].visible = selectedValues.has("Unassigned");
          } else if (arr.includes(val)) {
            this.rowData[i].visible = false;
          } else {
            this.rowData[i].visible = true;
          }
        }
      }
    },
    async sendChangeNotification() {
      const payload = {
        client: this.selectedJob.client,
        testers: this.showNotificationPopup,
        jobName: this.selectedJob.projectName,
        jobAddress: this.selectedJob.fullAddress,
        customer: this.selectedJob.company,
        region: this.selectedJob.region,
        jobTime: this.$moment(this.selectedJob.time).format("HH:mmA"),
        jobDate: this.selectedJob.date,
        meters: this.selectedJob.meters,
        accepted: this.selectedJob.accepted,
        requirements: this.selectedJob.requirements,
        mixCode: this.selectedJob.mixCode,
        clientName: this.selectedJob.contact,
        clientPhone: this.selectedJob.phone,
        clientEmail: this.selectedJob.email,
        status: this.selectedJob.status,
      };


      this.showNotificationPopup.forEach(async t => {
        var message = `Check your job board, this job has been removed: Job Name: ${this.selectedJob.projectName}, Address ${this.selectedJob.fullAddress}, Time: ${this.$moment(this.selectedJob.time).format('HH:mmA')}`
        if (!t.winningJob) {
          await postFire({
            collection: "messages",
            body: {
              to: t.phone,
              message: message,
              sent: false,
              twilio: null,
              twilioSid: "",
            },
          });
        }
      })


      console.log("payload", payload);
      this.axios
        .post(
          "https://us-central1-ktc-app-33f33.cloudfunctions.net/widgets/notify/change-job",
          payload
        )
        .then((response) => {
          this.showNotificationPopup = [];
        })
        .catch((e) => {
          console.log("Error sending notifications", e);
        });

      // Close Modals
      this.showNotificationPopup = [];
    },
    async handleChange(job, time, id, selectedDates) {

      console.log("time", time);
      console.log("selectedDates", selectedDates);
      await this.saveDate(job, selectedDates[0], id);
    },
    async saveDate(job, time, id) {
      console.log("time", time);
      await setFire({
        collection: "jobs",
        doc: id,
        body: {
          date: new Date(time),
        },
      });
      // Sort times
      this.rowData.sort((a, b) => new Date(a.time) - new Date(b.time));


      const _user = await this.getCurrentUser();

      await postFire({
        collection: "customerPosts",
        body: {
          postedBy: _user.uid,
          type: "timeUpdated",
          body: `Job time has been updated to ${new Date(job.time)}, for ${job.projectName} at ${job.fullAddress}.`,
          projectId: job.projectId,
          customerId: job.customerId,
          created: new Date(),
        }
      });


      // this.getItems()
    },
    async updateNote(job) {
      await setFire({
        collection: "jobs",
        doc: job.id,
        body: {
          notes: job.notes,
        },
      });
    },

    async getCurrentUser() {
      return new Promise((resolve, reject) => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
          unsubscribe();
          user ? resolve(user) : reject(new Error("No user is signed in."));
        });
      });
    },

    async updateStatus(job) {
      console.log("job for update status", job)
      await setFire({
        collection: "jobs",
        doc: job.id,
        body: {
          status: job.status,
        },
      });

      const _user = await this.getCurrentUser();

      await postFire({
        collection: "customerPosts",
        body: {
          postedBy: _user.uid,
          type: "statusUpdated",
          body: `Job status updated to ${job.status}, for ${job.projectName} at ${job.fullAddress} at ${job.time}.`,
          projectId: job.projectId,
          customerId: job.customerId,
          created: new Date(),
        }
      });

    },
    async removeTester(selectedJob) {
      await setFire({
        collection: "jobs",
        doc: selectedJob.id,
        body: {
          contractor: null,
          accepted: false,
        },
      });

      const _user = await this.getCurrentUser();

      await postFire({
        collection: "customerPosts",
        body: {
          postedBy: _user.uid,
          type: "testerRemoved",
          body: `Tester: ${selectedJob.tester} was removed for ${selectedJob.projectName} at ${selectedJob.fullAddress} at ${selectedJob.time}.`,
          projectId: selectedJob.projectId,
          customerId: selectedJob.customerId,
          created: new Date(),
        }
      });

      // await postFire({
      //       collection: "messages",
      //       body: {
      //         to: selectedJob.testerPhone,
      //         message: `A job has been removed: ${selectedJob.projectName} at ${selectedJob.fullAddress} at ${selectedJob.time}. Check your dashboard for updates.`,
      //         sent: false,
      //         twilio: null,
      //         twilioSid: "",
      //       },
      //     });

      this.showTesters = false;
    },
    async updateTester(tester, index, selectedJob) {
      console.log("selectedJob", selectedJob);
      if (selectedJob.tester) {
        // In this case we are changing a tester not adding a new one
        // So we want to show a popup

        this.showNotificationPopup = [
          // Winning job
          {
            name: tester.name,
            email: tester.email,
            phone: tester.phone,
            winningJob: true,
            selected: true,
          },
          // Loosing job
          {
            name: selectedJob.tester,
            email: selectedJob.testerEmail,
            phone: selectedJob.testerPhone,
            winningJob: false,
            selected: true,
          },
        ];
      }

      await setFire({
        collection: "jobs",
        doc: selectedJob.id,
        body: {
          contractor: tester,
          accepted: false,
        },
      });

      const _user = await this.getCurrentUser();

      await postFire({
        collection: "customerPosts",
        body: {
          postedBy: _user.uid,
          type: "testerAdded",
          body: `Tester added ${tester.name} for ${selectedJob.projectName} at ${selectedJob.fullAddress} at ${selectedJob.time}.`,
          projectId: selectedJob.projectId,
          customerId: selectedJob.customerId,
          created: new Date(),
        }
      });

      // Close modals
      this.showTesters = false;
    },
    rowSpan(field) {
      let val = 0; // or 2
      if (field == "status") val = 2;
      return val;
    },
    async cancelJob() {
      await setFire({
        collection: "jobs",
        doc: this.rowData[this.signOff].id,
        body: {
          status: "CANCELLED",
        },
      });
      this.signOff = -1;
      if (this.currentUser && this.currentUser.role === 'contractor') {
        this.showNextJobPrompt();
      }
    },
    async completeJob() {
      await setFire({
        collection: "jobs",
        doc: this.rowData[this.signOff].id,
        body: {
          status: "COMPLETED",
        },
      });
      this.signOff = -1;
      if (this.currentUser && this.currentUser.role === 'contractor') {
        this.showNextJobPrompt();
      }
    },
    showNextJobPrompt() {
      const sortedJobs = [...this.rowData]
        .filter(j => j.visible && j.status !== 'COMPLETED' && j.status !== 'CANCELLED')
        .sort((a, b) => new Date(a.time) - new Date(b.time));
      this.nextJob = sortedJobs.length > 0 ? sortedJobs[0] : null;
      this.nextJobModal = true;
    },
    getMinutesFromNow(time) {
      const diff = this.$moment(time).diff(this.$moment(), 'minutes');
      return diff > 0 ? diff : 0;
    },
    async sendEnRoute() {
      if (!this.enRouteJob) return;
      this.enRouteSending = true;
      try {
        const user = await this.getCurrentUser();
        const token = await user.getIdToken();
        const jobId = this.enRouteJob.id;

        await fetch(
          `https://us-central1-ktc-app-33f33.cloudfunctions.net/widgets/api/v1/messages/en-route/${jobId}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );

        // Update local state
        this.enRouteJob.enRoute = true;
        this.enRouteModal = false;
        this.enRouteJob = null;
      } catch (e) {
        console.error('Failed to send en-route:', e);
        alert('Failed to send en-route notification. Please try again.');
      } finally {
        this.enRouteSending = false;
      }
    },
    exportToCSV() {
      // Convert object array to CSV format
      const json2csvParser = new Parser();

      let j = this.visibleRows;
      for (let i = 0; i < j.length; i++) {
        j[i].time = this.$moment(j[i].time).format("HH:mmA");
      }

      const csvData = json2csvParser.parse(j);

      // Create a Blob object with CSV data
      const blob = new Blob([csvData], { type: "text/csv" });

      // Create a temporary anchor element
      const anchor = document.createElement("a");
      anchor.href = window.URL.createObjectURL(blob);
      anchor.download = "data.csv";

      // Programmatically click the anchor element to trigger the download
      anchor.click();

      // Clean up the temporary anchor element
      window.URL.revokeObjectURL(anchor.href);
      anchor.remove();
      bus.$emit("triggerExport", false);
    },
    async publishAll() {
      console.log("publishing all", this.visibleRows);
      if (this.currentUser.role == "admin") {
        console.log("finding emails or phone");
        let phoneNumbers = [];
        let emails = [];
        for (let i = 0; i < this.visibleRows.length; i++) {
          console.log("this.rowData[i].id", this.visibleRows[i].id);
          // Get all tester phone numbers make sure they are uniq
          // don't want to send multiple sms
          if (
            this.visibleRows[i].tester &&
            this.visibleRows[i].testerPhone &&
            !phoneNumbers.includes(this.visibleRows[i].testerPhone)
          ) {
            phoneNumbers.push(this.visibleRows[i].testerPhone);
          }
          // Email
          // send one email with all jobs
          console.log("this.visibleRows[i]", this.visibleRows[i]);
          console.log(
            "this.visibleRows[i].testerEmail",
            this.visibleRows[i].testerEmail
          );
          if (this.visibleRows[i].tester && this.visibleRows[i].testerEmail) {
            let _index;
            console.log(
              "this.visibleRows[i].testerEmail",
              this.visibleRows[i].testerEmail
            );
            // See if email object exists
            const _e = emails.filter((e) => {
              e.testerEmail == this.visibleRows[i].testerEmail;
            });

            console.log("_e", _e);
            // If it does then find the index and add job to the jobs array
            // else create a new object
            const _job = {
              projectId: this.visibleRows[i].projectId,
              projectName: this.visibleRows[i].projectName,
              date: this.visibleRows[i].date,
              time: this.$moment(this.visibleRows[i].time).format("HH:mmA"),
              client: this.visibleRows[i].client,
              company: this.visibleRows[i].company,
              customerId: this.visibleRows[i].customerId,
              customer: this.visibleRows[i].company,
              region: this.visibleRows[i].region,
              fullAddress: this.visibleRows[i].fullAddress,
              address: this.visibleRows[i].address,
              suburb: this.visibleRows[i].suburb,
              mixCode: this.visibleRows[i].mixCode,
              meters: this.visibleRows[i].meters,
              id: this.visibleRows[i].id,
              status: this.visibleRows[i].status,
              requirements: this.visibleRows[i].requirements,
              poNumber: this.visibleRows[i].poNumber,
              email: this.visibleRows[i].email,
              phone: this.visibleRows[i].phone,
              contact: this.visibleRows[i].contact,
              published: this.visibleRows[i].published,
            };
            if (_e.length > 0) {
              _index = emails.indexOf(_e);
              emails[_index].jobs.push(_job);
            } else {
              const obj = {
                email: this.visibleRows[i].testerEmail,
                jobs: [_job],
              };
              emails.push(obj);
            }
          }

          await setFire({
            collection: "jobs",
            doc: this.visibleRows[i].id,
            body: {
              published: true,
            },
          });
        }

        console.log("phoneNumbers", phoneNumbers);
        // Now loop phonenumbers and send message
        for (let i = 0; i < phoneNumbers.length; i++) {
          await postFire({
            collection: "messages",
            body: {
              to: phoneNumbers[i],
              message: `KCT Job board has been published for tomorrow - please sign in to see your jobs.`,
              sent: false,
              twilio: null,
              twilioSid: "",
            },
          });
        }
        // Loop through and send emails
        console.log("emails-", emails);
        // Consolodate array so all jobs appear in just one email object
        const consolidatedArray = emails.reduce((acc, current) => {
          const existingEntry = acc.find(
            (entry) => entry.email === current.email
          );

          if (existingEntry) {
            existingEntry.jobs = existingEntry.jobs.concat(current.jobs);
          } else {
            acc.push({ email: current.email, jobs: current.jobs });
          }

          return acc;
        }, []);

        for (let i = 0; i < consolidatedArray.length; i++) {
          console.log("consolidatedArray[i]", consolidatedArray[i]);

          this.axios.post(
            "https://us-central1-ktc-app-33f33.cloudfunctions.net/widgets/notify/publish-job",
            { jobs: consolidatedArray[i] }
          );
        }

        this.showPublishAll = false;
      } else {
        alert("you do not have access to publish jobs");
      }
    },
    showJobEdit(field, obj) {
      if (field != "tester") {
        // bus.$emit("updateJob", obj);
        this.$router.push({ name: "Jobs", query: { jobId: obj.id } });
      } else {
        this.showTesters = true;
        this.selectedJob = obj;
      }
    },
    async sendSms() {


      const _user = await this.getCurrentUser();

      if (this.selectedCustomerId != null) {
        console.log("this.smsTester", this.smsTester)
        await postFire({
          collection: "customerPosts",
          body: {
            body: `SMS sent to ${this.smsTester.tester} at ${this.smsTester.testerPhone} saying: ${this.smsBox}`,
            created: new Date(),
            customerId: this.selectedCustomerId,
            postedBy: _user.uid,
            type: "smsTester",
          },
        });
      }

      await postFire({
        collection: "messages",
        body: {
          to: this.smsTester.testerPhone,
          message: this.smsBox,
          sent: false,
          twilio: null,
          twilioSid: "",
        },
      });

      this.showTesters = false;
      this.smsModal = false;
    },
    normalizePhoneForSms(phone) {
      if (!phone) return '';
      let p = phone.replace(/[\s\-\(\)]/g, '');
      if (p.startsWith('04')) p = '+61' + p.slice(1);
      else if (p.startsWith('614') && !p.startsWith('+')) p = '+' + p;
      else if (p.startsWith('61') && !p.startsWith('+')) p = '+' + p;
      return p;
    },
    openTesterSmsModal(job) {
      this.selectedCustomerId = job.customerId;
      this.showTesters = true;
      this.smsModal = true;
      this.smsTester = job;
      this.selectedSmsTemplateId = '';
      this.smsBox = `Update to Job \n${job.projectName} \nStatus: #${job.status} \nClient: #${job.client} \nCustomer: #${job.customer}\nAddress: ${job.fullAddress}\nTime: #${job.time}\nMeters: #${job.meters}\nRequirements: #${job.requirements}`;
      this.loadSmsTemplates('single', job);
    },
    // `job` scopes the template list to that job's service. Omit it for bulk
    // sends, which then only offer templates that apply to every service.
    async loadSmsTemplates(type, job) {
      this.smsTemplateJob = job || null;
      try {
        const user = await this.getCurrentUser();
        const token = await user.getIdToken();
        const res = await fetch(
          'https://us-central1-ktc-app-33f33.cloudfunctions.net/widgets/api/v1/settings/sms/templates',
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const json = await res.json();
        const all = json.data || [];
        this.smsTemplates = type ? all.filter(t => t.type === type || t.type === 'all') : all;
      } catch (e) {
        console.error('Failed to load SMS templates:', e);
        this.smsTemplates = [];
      }
    },
    applySmsTemplate(messageField) {
      if (!this.selectedSmsTemplateId) return;
      const tpl = this.smsTemplates.find(t => t.id === this.selectedSmsTemplateId);
      if (!tpl) return;
      this[messageField] = tpl.body;
    },
    openChangeResolveModal(job) {
      this.changeResolveJob = job;
      this.changeResolveModal = true;
    },
    async resolveChangeRequest() {
      if (!this.changeResolveJob) return;
      await setFire({ collection: "jobs", doc: this.changeResolveJob.id, body: { clientSmsStatus: null, clientSmsResolvedBy: this.currentUser.email, clientSmsResolvedAt: new Date() } });
      this.changeResolveModal = false;
      this.changeResolveJob = null;
    },
    async openClientSmsModal(job) {
      // Generate client portal token for {link} shortcode
      if (job.id && !job.token) {
        try {
          job.token = await this.generateJobToken(job.id);
        } catch (e) {
          job.token = '';
        }
      }
      this.clientSmsJob = job;
      this.clientSmsPhone = this.normalizePhoneForSms(job.jobPhone || job.phone || '');
      this.clientSmsRecipient = 'primary';
      this.clientSmsManualPhone = '';
      this.selectedSmsTemplateId = '';
      const addr = job.formattedAddress || job.fullAddress || '';
      const dateStr = job.date || '';
      this.clientSmsMessage = `Hi, this is KCT. Please confirm your job at ${addr} on ${dateStr}. Reply YES to confirm or let us know if anything has changed.`;
      this.clientSmsModal = true;
      this.clientSmsHistory = [];
      this.clientSmsJobDirty = false;
      this.clientSmsJobSaving = false;
      this.clientSmsJobSaved = false;
      this.loadSmsTemplates('single', job);
      if (job.id) {
        await this.loadClientSmsHistory(job.id);
      }
    },
    async saveClientSmsJob() {
      const job = this.clientSmsJob;
      if (!job || !job.id) return;
      this.clientSmsJobSaving = true;
      try {
        const update = {
          status: job.status || 'ON-CALL',
          meters: job.meters || '',
          updated: new Date(),
        };
        if (job.date) {
          update.date = getTimeStamp(new Date(job.date));
        }
        await putFire({ collection: 'jobs', doc: job.id, body: update });
        this.clientSmsJobDirty = false;
        this.clientSmsJobSaved = true;
      } catch (e) {
        console.error('Failed to update job from SMS modal:', e);
      } finally {
        this.clientSmsJobSaving = false;
      }
    },
    async loadClientSmsHistory(jobId) {
      try {
        const user = await this.getCurrentUser();
        const token = await user.getIdToken();
        const res = await fetch(
          `https://us-central1-ktc-app-33f33.cloudfunctions.net/widgets/api/v1/messages/job/${jobId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await res.json();
        this.clientSmsHistory = data.data || data || [];
      } catch (e) {
        console.error('Failed to load SMS history:', e);
        this.clientSmsHistory = [];
      }
    },
    async sendClientSms() {
      if (!this.clientSmsMessage || !this.clientSmsJob) return;
      const job = this.clientSmsJob;
      const sends = [];

      if (this.clientSmsRecipient === 'primary') {
        const phone = this.normalizePhoneForSms(job.jobPhone || job.phone || '');
        if (!phone) { alert('No primary phone number.'); return; }
        sends.push({ phone, name: job.jobContact || job.contact || '' });
      } else if (this.clientSmsRecipient === 'secondary') {
        const phone = this.normalizePhoneForSms(job.secondaryPhone || '');
        if (!phone) { alert('No secondary phone number.'); return; }
        sends.push({ phone, name: job.secondaryContact || '' });
      } else if (this.clientSmsRecipient === 'both') {
        const p1 = this.normalizePhoneForSms(job.jobPhone || job.phone || '');
        const p2 = this.normalizePhoneForSms(job.secondaryPhone || '');
        if (!p1 && !p2) { alert('No phone numbers available.'); return; }
        if (p1) sends.push({ phone: p1, name: job.jobContact || job.contact || '' });
        if (p2) sends.push({ phone: p2, name: job.secondaryContact || '' });
      } else if (this.clientSmsRecipient === 'manual') {
        const phone = this.normalizePhoneForSms(this.clientSmsManualPhone || '');
        if (!phone) { alert('Please enter a phone number.'); return; }
        sends.push({ phone, name: '' });
      }

      this.clientSmsLoading = true;
      try {
        const user = await this.getCurrentUser();
        const token = await user.getIdToken();
        const jobId = job.id;
        for (const s of sends) {
          const link = job.token ? `${window.location.origin}/job/${job.token}` : '';
          const rendered = this.clientSmsMessage
            .replace(/\{recipientName\}/g, s.name)
            .replace(/\{receiverName\}/g, s.name)
            .replace(/\{company\}/g, job.company || '')
            .replace(/\{contact\}/g, job.jobContact || job.contact || '')
            .replace(/\{address\}/g, job.formattedAddress || job.fullAddress || '')
            .replace(/\{date\}/g, job.date || '')
            .replace(/\{time\}/g, job.time || '')
            .replace(/\{meters\}/g, job.meters || '')
            .replace(/\{project\}/g, job.projectName || '')
            .replace(/\{link\}/g, link);
          const message = applyCustomFieldTokens(this.customFieldDefs, job, rendered);
          await fetch(
            `https://us-central1-ktc-app-33f33.cloudfunctions.net/widgets/api/v1/messages/job/${jobId}`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({ message, to: s.phone }),
            }
          );
        }
        if (jobId) {
          await this.loadClientSmsHistory(jobId);
        }
        this.clientSmsMessage = '';
      } catch (e) {
        console.error('Failed to send client SMS:', e);
        alert('Failed to send SMS. Please try again.');
      } finally {
        this.clientSmsLoading = false;
      }
    },
    formatSmsTimestamp(ts) {
      if (!ts) return '';
      const ms = ts.seconds ? ts.seconds * 1000 : ts._seconds ? ts._seconds * 1000 : new Date(ts).getTime();
      const d = new Date(ms);
      const pad = (n) => String(n).padStart(2, '0');
      return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    async openLinkModal(job) {
      this.linkJob = job;
      this.linkCopied = false;
      const secret = 'kct-client-link-s3cret-2024';
      const encoder = new TextEncoder();
      const key = await crypto.subtle.importKey(
        'raw', encoder.encode(secret), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']
      );
      const sig = await crypto.subtle.sign('HMAC', key, encoder.encode(job.id));
      const hex = Array.from(new Uint8Array(sig)).map(b => b.toString(16).padStart(2, '0')).join('');
      const token = `${job.id}~${hex.slice(0, 16)}`;
      this.linkUrl = `${window.location.origin}/job/${token}`;
      this.linkModal = true;
    },
    async copyLink() {
      try {
        await navigator.clipboard.writeText(this.linkUrl);
        this.linkCopied = true;
        setTimeout(() => { this.linkCopied = false; }, 2000);
      } catch (e) {
        const input = document.createElement('input');
        input.value = this.linkUrl;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
        this.linkCopied = true;
        setTimeout(() => { this.linkCopied = false; }, 2000);
      }
    },
    async generateJobToken(jobId) {
      const secret = 'kct-client-link-s3cret-2024';
      const encoder = new TextEncoder();
      const key = await crypto.subtle.importKey(
        'raw', encoder.encode(secret), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']
      );
      const sig = await crypto.subtle.sign('HMAC', key, encoder.encode(jobId));
      const hex = Array.from(new Uint8Array(sig)).map(b => b.toString(16).padStart(2, '0')).join('');
      return `${jobId}~${hex.slice(0, 16)}`;
    },
    async openBulkSmsModal() {
      this.bulkSmsModal = true;
      this.bulkSmsSending = false;
      this.bulkSmsDone = false;
      this.bulkSmsResults = [];
      this.bulkSmsSent = 0;
      this.bulkSmsTotal = 0;
      this.bulkSmsGroupByContact = true;
      this.bulkSmsRecipientType = 'primary';
      this.selectedSmsTemplateId = '';
      this.loadSmsTemplates('bulk');

      // Default message template
      this.bulkSmsMessage = 'Hi {contact}, this is KCT confirming your job at {address} on {date} at {time}. View details: {link}';

      // Build recipients from visible rows that are active
      const rows = this.visibleRows.filter(r =>
        r.status !== 'COMPLETED' && r.status !== 'CANCELLED'
      );

      // Generate tokens for all jobs
      const tokenMap = {};
      for (const r of rows) {
        if (r.id && !tokenMap[r.id]) {
          try {
            tokenMap[r.id] = await this.generateJobToken(r.id);
          } catch (e) {
            tokenMap[r.id] = '';
          }
        }
      }

      // Group by normalized primary phone
      const byPhone = {};
      for (const r of rows) {
        const phone = this.normalizePhoneForSms(r.jobPhone || r.phone);
        if (!phone) continue;
        if (!byPhone[phone]) {
          byPhone[phone] = {
            phone,
            contact: r.jobContact || r.contact || '',
            company: r.company || '',
            clientSmsStatus: r.clientSmsStatus,
            jobs: [],
          };
        }
        byPhone[phone].jobs.push({
          id: r.id,
          status: r.status || '',
          company: r.company || '',
          contact: r.jobContact || r.contact || '',
          secondaryContact: r.secondaryContact || '',
          secondaryPhone: r.secondaryPhone || '',
          projectName: r.projectName || '',
          fullAddress: r.formattedAddress || r.fullAddress || '',
          formattedAddress: r.formattedAddress || r.fullAddress || '',
          date: r.date || '',
          time: r.time ? this.$moment(r.time).format('h:mmA') : '',
          meters: r.meters || '',
          token: tokenMap[r.id] || '',
        });
      }

      this.bulkSmsRecipients = Object.values(byPhone).map(r => ({
        ...r,
        selected: !!r.phone,
        jobCount: r.jobs.length,
      }));
    },
    insertShortcode(code) {
      this.bulkSmsMessage += code;
    },
    insertClientSmsShortcode(code) {
      this.clientSmsMessage += code;
    },
    bulkSmsSelectAll() {
      this.bulkSmsRecipients.forEach(r => { if (r.phone) r.selected = true; });
    },
    bulkSmsDeselectAll() {
      this.bulkSmsRecipients.forEach(r => { r.selected = false; });
    },
    bulkSmsSelectByStatus(status) {
      this.bulkSmsRecipients.forEach(r => {
        r.selected = r.phone && r.jobs.some(j => j.status === status);
      });
    },
    closeBulkSms() {
      this.bulkSmsModal = false;
      this.bulkSmsSending = false;
      this.bulkSmsDone = false;
    },
    buildSmsBody(template, job) {
      const link = job.token ? `${window.location.origin}/job/${job.token}` : '';
      const body = template
        .replace(/\{recipientName\}/g, job.receiverName || job.jobContact || job.contact || '')
        .replace(/\{receiverName\}/g, job.receiverName || job.jobContact || job.contact || '')
        .replace(/\{company\}/g, job.company || '')
        .replace(/\{contact\}/g, job.jobContact || job.contact || '')
        .replace(/\{address\}/g, job.formattedAddress || job.fullAddress || '')
        .replace(/\{date\}/g, job.date || '')
        .replace(/\{time\}/g, job.time || '')
        .replace(/\{meters\}/g, job.meters || '')
        .replace(/\{project\}/g, job.projectName || '')
        .replace(/\{link\}/g, link);
      return applyCustomFieldTokens(this.customFieldDefs, job, body);
    },
    async sendBulkSms() {
      const selected = this.bulkSmsRecipients.filter(r => r.selected && r.phone);
      if (!selected.length || !this.bulkSmsMessage.trim()) return;

      this.bulkSmsSending = true;
      this.bulkSmsDone = false;
      this.bulkSmsResults = [];

      // Build the recipients array for the API
      const recipients = [];
      const rType = this.bulkSmsRecipientType;

      for (const r of selected) {
        // Determine which sends to create based on recipient type
        // Each entry: { phone, receiverName, jobs }
        const sendTargets = [];

        if (rType === 'secondary') {
          // Use secondary phone/contact for all jobs under this recipient
          const jobs = r.jobs.filter(j => j.secondaryPhone);
          if (jobs.length > 0) {
            const secPhone = this.normalizePhoneForSms(jobs[0].secondaryPhone);
            if (secPhone) {
              const taggedJobs = jobs.map(j => ({ ...j, receiverName: j.secondaryContact || j.contact }));
              sendTargets.push({ phone: secPhone, receiverName: jobs[0].secondaryContact || '', jobs: taggedJobs });
            }
          }
        } else if (rType === 'both') {
          // Primary
          const priJobs = r.jobs.map(j => ({ ...j, receiverName: j.contact }));
          sendTargets.push({ phone: r.phone, receiverName: r.contact, jobs: priJobs });
          // Secondary (only jobs that have a secondary phone)
          const secJobs = r.jobs.filter(j => j.secondaryPhone);
          if (secJobs.length > 0) {
            const secPhone = this.normalizePhoneForSms(secJobs[0].secondaryPhone);
            if (secPhone && secPhone !== r.phone) {
              const taggedJobs = secJobs.map(j => ({ ...j, receiverName: j.secondaryContact || j.contact }));
              sendTargets.push({ phone: secPhone, receiverName: secJobs[0].secondaryContact || '', jobs: taggedJobs });
            }
          }
        } else {
          // primary (default)
          const priJobs = r.jobs.map(j => ({ ...j, receiverName: j.contact }));
          sendTargets.push({ phone: r.phone, receiverName: r.contact, jobs: priJobs });
        }

        for (const target of sendTargets) {
          if (this.bulkSmsGroupByContact) {
            let message = '';
            if (target.jobs.length === 1) {
              message = this.buildSmsBody(this.bulkSmsMessage, target.jobs[0]);
            } else {
              const firstJob = target.jobs[0];
              message = `Hi ${target.receiverName || firstJob.company}, this is KCT. You have ${target.jobs.length} jobs scheduled:\n`;
              target.jobs.forEach((j, idx) => {
                const link = `${window.location.origin}/job/${j.token}`;
                message += `\n${idx + 1}. ${j.projectName || j.fullAddress}${j.time ? ' at ' + j.time : ''}${j.meters ? ' (' + j.meters + 'm³)' : ''}\nDetails: ${link}`;
              });
            }
            recipients.push({
              phone: target.phone,
              message,
              jobIds: target.jobs.map(j => j.id),
            });
          } else {
            for (const job of target.jobs) {
              recipients.push({
                phone: target.phone,
                message: this.buildSmsBody(this.bulkSmsMessage, job),
                jobIds: [job.id],
              });
            }
          }
        }
      }

      this.bulkSmsTotal = recipients.length;
      this.bulkSmsSent = 0;

      try {
        const user = await this.getCurrentUser();
        const token = await user.getIdToken();

        // Send in batches of 5 for progress visibility
        const batchSize = 5;
        for (let i = 0; i < recipients.length; i += batchSize) {
          const batch = recipients.slice(i, i + batchSize);
          const res = await fetch(
            'https://us-central1-ktc-app-33f33.cloudfunctions.net/widgets/api/v1/messages/bulk',
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({ recipients: batch }),
            }
          );
          const data = await res.json();
          if (data.data && data.data.results) {
            this.bulkSmsResults.push(...data.data.results);
          }
          this.bulkSmsSent += batch.length;
        }
      } catch (e) {
        console.error('Bulk SMS error:', e);
        // Mark remaining as failed
        const sent = this.bulkSmsResults.length;
        for (let i = sent; i < recipients.length; i++) {
          this.bulkSmsResults.push({ phone: recipients[i].phone, success: false, error: 'Network error' });
        }
        this.bulkSmsSent = this.bulkSmsTotal;
      }

      this.bulkSmsDone = true;
      this.bulkSmsSending = false;
    },
    async updateJob(index) {
      await setFire({
        collection: "jobs",
        doc: this.rowData[index].id,
        body: {
          published: this.rowData[index].published,
        },
      });

      if (this.rowData[index].published) {
        // Send notification to job published
        const _job = {
          projectId: this.rowData[index].projectId,
          projectName: this.rowData[index].projectName,
          date: this.rowData[index].date,
          time: this.$moment(this.rowData[index].time).format("HH:mmA"),
          client: this.rowData[index].client,
          company: this.rowData[index].company,
          customerId: this.rowData[index].customerId,
          customer: this.rowData[index].company,
          region: this.rowData[index].region,
          fullAddress: this.rowData[index].fullAddress,
          address: this.rowData[index].address,
          suburb: this.rowData[index].suburb,
          mixCode: this.rowData[index].mixCode,
          meters: this.rowData[index].meters,
          accepted: this.rowData[index].accepted,
          id: this.rowData[index].id,
          status: this.rowData[index].status,
          requirements: this.rowData[index].requirements,
          poNumber: this.rowData[index].poNumber,
          email: this.rowData[index].email,
          phone: this.rowData[index].phone,
          contact: this.rowData[index].contact,
          published: this.rowData[index].published,
        };

        const arr = {
          email: this.rowData[index].testerEmail,
          jobs: [_job],
        };

        this.axios.post(
          "https://us-central1-ktc-app-33f33.cloudfunctions.net/widgets/notify/publish-job",
          {
            jobs: arr,
          }
        );
        // Send sms
        await postFire({
          collection: "messages",
          body: {
            to: this.rowData[index].testerPhone,
            message: `A job has been published to your board - ${_job.projectName}. Please check the dashboard for details.`,
            sent: false,
            twilio: null,
            twilioSid: "",
          },
        });
      }
    },
    sortCol(colIndex) {
      this.currentlySortedBy = colIndex;
      if (this.columnDefs[colIndex].sortDirection == "asc")
        this.columnDefs[colIndex].sortDirection = "des";
      else this.columnDefs[colIndex].sortDirection = "asc";

      // this.rowData = sortJsonArray(
      //   this.rowData,
      //   this.columnDefs[colIndex].field,
      //   this.columnDefs[colIndex].sortDirection
      // );
    },
    getUniqs(field, _arr) {
      let arr = [];
      for (let i = 0; i < _arr.length; i++) {
        console.log("uniqArr", _arr[i]);
        if (!arr.includes(_arr[i])) {
          arr.push(_arr[i]);
        }
      }

      return arr;
    },
    getVisible(items) {
      console.log("items", items);
      const a = items.filter((i) => i.visible);
      console.log("a", a);
      return a;
    },
    filterResults() {
      const prevDate = this.date.split("&")[0];
      const date = this.date.split("&")[1];

      const url =
        "https://us-central1-ktc-app-33f33.cloudfunctions.net/widgets/search";
      const d = this.$moment(prevDate).startOf("day").format("x");
      const ld = this.$moment(date).endOf("day").format("x");
      let queryStr = `date:${d} TO ${ld}`;
      this.columnDefs.forEach((c) => {
        this.getVisible(c.filterValueArray).forEach((f, index) => {
          let andOr = "";
          if (queryStr.split("").length > 0) andOr = " AND";
          if (index > 0) andOr = " OR";

          const str = `${andOr} ${c.filterField}:'${f.value}'`;

          if (f.visible) queryStr = queryStr.concat(str);
        });
      });

      this.axios
        .get(url, {
          params: {
            indexName: "jobs",
            filter: queryStr,
            search: this.search,
          },
        })
        .then(async (response) => {
          await this.formatJob(response.data, true);
        })
        .catch((e) => {
          console.log("Error algolia", e);
        });
    },

    showWorkCard(i) {
      bus.$emit("showWorkCardFunc", i);
    },
    jobStyles(job) {
      if (job.status == "ON-CALL")
        return "bgyellowstripes text-white bg-yellow-600";
      if (job.status == "FIRM") return "bg-green-200 text-green-600";
      if (job.status == "COMMITTED") return "text-white bg-blue-600";
      if (job.status == "COMPLETED")
        return "bggreenstripes text-white bg-green-600";
      if (job.status == "CANCELLED") return " text-white bg-red-800";
    },
    testerStyle(job) {
      if (!job.tester)
        return "rounded-full px-3 text-xs py-1 bgredstripes text-white bg-red-900";
    },
    // A team leader sees jobs allocated to their team, plus any job where they
    // are the assigned tester (a leader can also work jobs themselves).
    teamLeaderCanSeeJob(job) {
      if (!job || !this.currentUser) return false;
      const ids = job.teamLeaderIds || [];
      if (ids.includes(this.currentUser.id)) return true;
      return !!(job.contractor && job.contractor.id === this.currentUser.id);
    },
    async getItems() {
      // Unsubscribe previous listener to prevent duplicate data
      if (unsubscribe) {
        unsubscribe();
        unsubscribe = null;
      }
      this.rowData = [];
      if (this.date) {
        const prevDate = this.date.split("&")[0];
        const date = this.date.split("&")[1];
        const start = this.$moment(prevDate).startOf("day");
        const end = this.$moment(date).endOf("day");

        let payload = {
          collection: "jobs",
          dateFrom: getTimeStamp(new Date(start)),
          dateTo: getTimeStamp(new Date(end)),
          contractor: null,
        };

        if (this.currentUser.role != "admin" && !this.isTeamLeader)
          payload.contractor = this.currentUser.id;

        let q;
        if (this.isTeamLeader) {
          // Team leaders: load all jobs for date range, filter client-side by teamLeaderIds
          q = query(
            collection(db, "jobs"),
            where("date", ">", payload.dateFrom),
            where("date", "<", payload.dateTo),
            orderBy("date")
          );
        } else if (payload.contractor) {
          // Show result only where contactor is equal to payload.tester
          // also testers can only see published jobs
          q = query(
            collection(db, "jobs"),
            where("date", ">", payload.dateFrom),
            where("date", "<", payload.dateTo),
            where("contractor.id", "==", payload.contractor),
            where("published", "==", true),
            orderBy("date")
          );
        } else {
          q = query(
            collection(db, "jobs"),
            where("date", ">", payload.dateFrom),
            where("date", "<", payload.dateTo),
            orderBy("date")
          );
        }

        // Track IDs already in rowData to prevent duplicates from async race conditions
        const knownIds = new Set(this.rowData.map(j => j.id));

        unsubscribe = onSnapshot(q, async (snapshot) => {
          // If the snapshot is empty on first load, stop loading
          if (snapshot.empty && !this.initialLoadComplete) {
            this.initialLoadComplete = true;
            this.scheduleLoading = false;
          }

          const changes = snapshot.docChanges();

          // Process changes sequentially to avoid async race conditions
          for (const change of changes) {
            if (change.type === "added") {
              const id = change.doc.id;
              if (knownIds.has(id)) continue;
              const rawData = change.doc.data();
              // Team leader: skip jobs not assigned to this team leader or to them as tester
              if (this.isTeamLeader && !this.teamLeaderCanSeeJob(rawData)) continue;
              knownIds.add(id);
              const formatJob = await this.formatJob(
                [{ ...rawData, id }],
                false,
                true
              );
              this.rowData.push(formatJob);
              this.rowData.sort((a, b) => new Date(a.time) - new Date(b.time));
              this.filterObjects();
            }
            if (change.type === "modified") {
              const rawData = change.doc.data();
              // Team leader: handle jobs added/removed from this team leader
              if (this.isTeamLeader) {
                const inList = this.rowData.findIndex(j => j.id === change.doc.id) !== -1;
                if (!this.teamLeaderCanSeeJob(rawData)) {
                  // Remove if no longer assigned
                  if (inList) {
                    const ri = this.rowData.findIndex(j => j.id === change.doc.id);
                    knownIds.delete(change.doc.id);
                    this.rowData.splice(ri, 1);
                    this.filterObjects();
                  }
                  continue;
                } else if (!inList) {
                  // Newly assigned to this team leader
                  knownIds.add(change.doc.id);
                  const fj = await this.formatJob([{ ...rawData, id: change.doc.id }], false, true);
                  this.rowData.push(fj);
                  this.rowData.sort((a, b) => new Date(a.time) - new Date(b.time));
                  this.filterObjects();
                  continue;
                }
              }
              const index = this.rowData.findIndex(
                (job) => job.id === change.doc.id
              );
              if (index !== -1) {
                const oldStatus = this.rowData[index].clientSmsStatus;
                const newStatus = rawData.clientSmsStatus || null;

                const formatJob = await this.formatJob(
                  [{ ...rawData, id: change.doc.id }],
                  false,
                  true
                );
                this.rowData.splice(index, 1, formatJob);
                this.filterObjects();

                // Detect clientSmsStatus change and notify
                if (this.initialLoadComplete && newStatus && newStatus !== oldStatus) {
                  const _d = rawData.date ? (rawData.date.seconds ? new Date(rawData.date.seconds * 1000) : new Date(rawData.date)) : null;
                  const dateStr = _d ? this.$moment(_d).format('DD MMM YYYY h:mmA') : '';
                  const notification = {
                    status: newStatus,
                    company: rawData.company || '',
                    projectName: rawData.projectName || '',
                    date: dateStr,
                    address: rawData.formattedAddress || '',
                    jobId: change.doc.id,
                    message: rawData.clientSmsMessage || '',
                    timestamp: Date.now(),
                    read: false,
                  };
                  this.$store.commit('addClientNotification', notification);

                  if (newStatus === 'CHANGED' || newStatus === 'CANCELLED' ||
                      (newStatus === 'FIRM' && (oldStatus === 'CHANGED' || oldStatus === 'CANCELLED'))) {
                    this.clientAlert = notification;
                  }
                }
              }
            }
            if (change.type === "removed") {
              const index = this.rowData.findIndex(
                (job) => job.id === change.doc.id
              );
              if (index !== -1) {
                knownIds.delete(change.doc.id);
                this.rowData.splice(index, 1);
                this.filterObjects();
              }
            }
          }

          // Mark initial load as done after first snapshot
          if (!this.initialLoadComplete) {
            this.$nextTick(() => {
              this.initialLoadComplete = true;
              this.scheduleLoading = false;
            });
          }
        });
      }

      // console.log(":_jobs", _jobs)
    },
    openStartTimesModal() {
      const testerMap = {};
      this.rowData.forEach(job => {
        if (!job.tester) return;
        const key = job.tester;
        if (!testerMap[key]) {
          testerMap[key] = { testerName: job.tester, testerId: job.testerId || null, jobs: [] };
        }
        if (!testerMap[key].testerId && job.testerId) testerMap[key].testerId = job.testerId;
        testerMap[key].jobs.push(job);
      });

      // Build entries for testers WITH jobs
      const entries = Object.values(testerMap).map(t => {
        const sortedJobs = [...t.jobs].sort((a, b) => new Date(a.time) - new Date(b.time));
        const firstJob = sortedJobs[0];
        const firstJobTime = new Date(firstJob.time);
        const defaultStart = new Date(firstJobTime.getTime() - 30 * 60000);
        const existingStart = firstJob.testerStartTime || null;
        const startTime = existingStart ||
          `${String(defaultStart.getHours()).padStart(2, '0')}:${String(defaultStart.getMinutes()).padStart(2, '0')}`;

        return {
          testerName: t.testerName,
          testerId: t.testerId,
          startTime: startTime,
          dayOff: false,
          firstJobAddress: firstJob.fullAddress || firstJob.formattedAddress || '',
          firstJobTime: this.$moment(firstJobTime).format('h:mmA'),
          jobIds: t.jobs.map(j => j.id),
        };
      });

      // Add testers WITHOUT jobs (day off by default)
      const assignedNames = Object.keys(testerMap);
      this.testers.forEach(t => {
        if (!t.name) return;
        if (assignedNames.includes(t.name)) return;
        entries.push({
          testerName: t.name,
          testerId: t.id || null,
          startTime: '',
          dayOff: true,
          firstJobAddress: '',
          firstJobTime: '',
          jobIds: [],
        });
      });

      // Sort: working testers by start time first, then day-off testers alphabetically
      entries.sort((a, b) => {
        if (a.dayOff !== b.dayOff) return a.dayOff ? 1 : -1;
        if (!a.dayOff && !b.dayOff) return (a.startTime || '').localeCompare(b.startTime || '');
        return (a.testerName || '').localeCompare(b.testerName || '');
      });

      this.startTimeEntries = entries;
      this.showStartTimes = true;
    },
    async saveStartTimes() {
      for (const entry of this.startTimeEntries) {
        // Save testerStartTime to each job document
        for (const jobId of entry.jobIds) {
          await setFire({
            collection: 'jobs',
            doc: jobId,
            body: { testerStartTime: entry.startTime },
          });
        }
        this.rowData.forEach(job => {
          if (entry.jobIds.includes(job.id)) {
            job.testerStartTime = entry.startTime;
          }
        });

        // Also save to timesheets collection so tester profile shows correct start time
        if (entry.testerId && entry.jobIds.length > 0) {
          const firstJob = this.rowData.find(j => entry.jobIds.includes(j.id));
          if (firstJob && firstJob.time) {
            const dateStr = this.$moment(firstJob.time).format('YYYY-MM-DD');
            await setFire({
              collection: 'timesheets',
              doc: `${dateStr}_${entry.testerId}`,
              body: {
                contractorId: entry.testerId,
                contractorName: entry.testerName,
                date: dateStr,
                scheduledStart: entry.startTime,
                savedAt: new Date(),
              },
            });
          }
        }
      }
      this.showStartTimes = false;
    },
    formatStartTime(time) {
      if (!time) return '';
      return this.$moment(time, 'HH:mm').format('h:mm A');
    },
    columnLabel(field) {
      const labels = {
        date: 'Date',
        time: 'Time',
        client: 'Client',
        projectName: 'Project',
        customer: 'Customer',
        region: 'Region',
        fullAddress: 'Address',
        mixCode: 'Mix Code',
        meters: 'Meters',
        duration: 'Duration',
        requirements: 'Requirements',
        teamLeader: 'Team Leader',
        tester: 'Tester',
        accepted: 'Accepted',
        status: 'Status',
      };
      if (labels[field]) return labels[field];
      const def = allFieldDefs(this.customFieldDefs).find((f) => f.key === field);
      return (def && def.label) || field;
    },
    // ==================== PER-SERVICE CUSTOM FIELDS ====================
    rowMatchesService(row) {
      if (!this.serviceFilterActive) return true;
      return resolveService(row) === this.selectedService;
    },
    // Tester-visible fields for one row, for the contractor and team-leader
    // views. Rows carry the custom keys via customFieldRowValues().
    testerFields(row) {
      if (!this.customFieldsEnabled) return [];
      return resolveFieldValues(this.customFieldDefs, row, "tester");
    },
    // Row values for the generic `{{ i[b.field] }}` cell. Returns {} while the
    // feature is off so rows — and therefore the CSV export — are unchanged.
    customFieldRowValues(job) {
      if (!this.customFieldsEnabled) return {};
      const out = { serviceType: resolveService(job) };
      allFieldKeys(this.customFieldDefs).forEach((key) => {
        if (job[key] !== undefined) out[key] = job[key];
      });
      return out;
    },
    // columnDefs is mutated at runtime (sortDirection, filterValueArray,
    // _origIndex) so it can't be a computed — splice custom columns in instead.
    rebuildColumnDefs() {
      const base = this.baseColumnFields;
      const customKeys = allFieldKeys(this.customFieldDefs).filter(
        (k) => !base.includes(k)
      );
      for (let i = this.columnDefs.length - 1; i >= 0; i--) {
        if (customKeys.includes(this.columnDefs[i].field)) {
          this.columnDefs.splice(i, 1);
        }
      }
      if (this.serviceFilterActive) {
        // Fields already in columnDefs (mixCode, meters) must not be duplicated.
        const add = fieldsFor(this.customFieldDefs, this.selectedService, "schedule")
          .filter((f) => !base.includes(f.key));
        let at = this.columnDefs.findIndex((c) => c.field === "duration");
        at = at === -1 ? this.columnDefs.length : at + 1;
        add.forEach((f, n) => {
          this.columnDefs.splice(at + n, 0, {
            field: f.key,
            width: "w-20",
            filterField: f.key,
            openFilter: false,
            filterValue: "",
            filterValueArray: [],
            sortDirection: "asc",
          });
        });
      }
      this.initColumnConfig();
    },
    async onServiceChange(serviceKey) {
      this.selectedService = serviceKey;
      this.rebuildColumnDefs();
      if (!this.currentUser || !this.currentUser.id) return;
      try {
        await putFire({
          collection: "users",
          doc: this.currentUser.id,
          body: { scheduleSelectedService: serviceKey },
        });
        this.$store.commit("updateState", {
          state: "currentUser",
          body: { ...this.currentUser, scheduleSelectedService: serviceKey },
        });
      } catch (e) {
        console.error("Could not save selected service:", e);
      }
    },
    // The team leader table has its own composite columns (Date/Time, Mix/m3,
    // Contact...) so it can't share the admin columnDefs.
    teamLeaderColumnDefs() {
      return [
        { field: 'datetime',     label: 'Date / Time' },
        { field: 'project',      label: 'Project' },
        { field: 'address',      label: 'Address' },
        { field: 'mix',          label: 'Mix / m\u00B3' },
        { field: 'requirements', label: 'Requirements' },
        { field: 'contact',      label: 'Contact' },
        { field: 'tester',       label: 'Tester' },
        { field: 'teamLeader',   label: 'Team Leaders' },
        { field: 'status',       label: 'Status' },
        { field: 'published',    label: 'Publish' },
        { field: 'notes',        label: 'Notes' },
        { field: 'actions',      label: 'Actions' },
      ];
    },
    teamLeaderColSizeWidths() {
      return {
        datetime:     { S: 90,  M: 110, L: 150 },
        project:      { S: 110, M: 160, L: 260 },
        address:      { S: 140, M: 200, L: 320 },
        mix:          { S: 70,  M: 90,  L: 130 },
        requirements: { S: 110, M: 160, L: 260 },
        contact:      { S: 100, M: 140, L: 200 },
        tester:       { S: 110, M: 150, L: 200 },
        teamLeader:   { S: 100, M: 130, L: 180 },
        status:       { S: 100, M: 130, L: 180 },
        published:    { S: 100, M: 130, L: 170 },
        notes:        { S: 120, M: 170, L: 260 },
        actions:      { S: 160, M: 190, L: 240 },
      };
    },
    tlColStyle(field) {
      const w = this.teamLeaderColWidthMap[field] || 120;
      return { minWidth: w + 'px', width: w + 'px', maxWidth: w + 'px' };
    },
    colSizeWidths() {
      if (this.isTeamLeader) return this.teamLeaderColSizeWidths();
      return {
        date:         { S: 60,  M: 80,  L: 110 },
        time:         { S: 60,  M: 80,  L: 110 },
        client:       { S: 50,  M: 70,  L: 100 },
        projectName:  { S: 100, M: 160, L: 260 },
        customer:     { S: 80,  M: 120, L: 200 },
        region:       { S: 60,  M: 100, L: 160 },
        fullAddress:  { S: 140, M: 260, L: 400 },
        mixCode:      { S: 50,  M: 70,  L: 100 },
        meters:       { S: 50,  M: 70,  L: 100 },
        duration:     { S: 50,  M: 70,  L: 100 },
        requirements: { S: 100, M: 160, L: 260 },
        teamLeader:   { S: 70,  M: 100, L: 150 },
        tester:       { S: 70,  M: 100, L: 150 },
        accepted:     { S: 50,  M: 70,  L: 100 },
        status:       { S: 80,  M: 130, L: 180 },
      };
    },
    getDefaultColumnConfig() {
      if (this.isTeamLeader) {
        return this.teamLeaderColumnDefs().map(col => ({
          field: col.field,
          label: col.label,
          visible: true,
          fixed: false,
          size: 'M',
        }));
      }
      const defaultFixed = ['tester', 'accepted', 'status'];
      return this.columnDefs.map(col => ({
        field: col.field,
        label: this.columnLabel(col.field),
        visible: true,
        fixed: defaultFixed.includes(col.field),
        size: 'M',
      }));
    },
    // Team leaders and admins see different tables, so their preferences are
    // stored under separate keys on the user doc.
    columnConfigKey() {
      if (this.isTeamLeader) return 'teamLeaderColumnConfig';
      // All Services and Concrete Testing share the bare key so every existing
      // admin's saved layout survives untouched.
      if (this.serviceFilterActive && this.selectedService !== DEFAULT_SERVICE) {
        return `scheduleColumnConfig__${this.selectedService}`;
      }
      return 'scheduleColumnConfig';
    },
    initColumnConfig() {
      const saved = this.currentUser && this.currentUser[this.columnConfigKey()];
      const defaults = this.getDefaultColumnConfig();
      if (saved && saved.length > 0) {
        const savedFields = saved.map(c => c.field);
        const merged = saved.map(c => {
          const def = defaults.find(d => d.field === c.field);
          // Migrate old width-based config to size-based, drop legacy width
          const migrated = { ...c, size: c.size || (def ? def.size : 'M') };
          delete migrated.width;
          return migrated;
        });
        defaults.forEach(d => {
          if (!savedFields.includes(d.field)) {
            merged.push({ ...d });
          }
        });
        const validFields = defaults.map(c => c.field);
        this.columnConfig = merged.filter(c => validFields.includes(c.field));
      } else {
        this.columnConfig = defaults;
      }
    },
    openColumnSettings() {
      this.modalColumnConfig = JSON.parse(JSON.stringify(this.columnConfig));
      this.showColumnSettings = true;
    },
    setColSize(field, size) {
      // Update modal state
      const mc = this.modalColumnConfig.find(c => c.field === field);
      if (mc) mc.size = size;
      // Apply live to the table so widths update reactively
      const idx = this.columnConfig.findIndex(c => c.field === field);
      if (idx !== -1) {
        this.$set(this.columnConfig, idx, { ...this.columnConfig[idx], size });
      }
    },
    resetColumnConfig() {
      this.modalColumnConfig = this.getDefaultColumnConfig();
      // Apply live
      this.columnConfig = JSON.parse(JSON.stringify(this.modalColumnConfig));
    },
    async saveColumnConfig() {
      this.columnConfig = JSON.parse(JSON.stringify(this.modalColumnConfig));
      this.showColumnSettings = false;
      const uid = this.currentUser.id;
      const key = this.columnConfigKey();
      await putFire({
        collection: 'users',
        doc: uid,
        body: { [key]: this.columnConfig },
      });
      // Update Vuex store so vuex-persist keeps the config across reloads
      this.$store.commit('updateState', {
        state: 'currentUser',
        body: { ...this.currentUser, [key]: this.columnConfig },
      });
    },
    filterObjects() {
      this.columnDefs.forEach((c, indexOfO) => {
        const rows = this.rowData.filter((obj) => obj["filter"] !== "");
        // teamLeader holds a joined string of many leaders — list each one separately
        const fields = c.field === "teamLeader"
          ? rows.reduce((acc, obj) => acc.concat(this.teamLeaderNames(obj)), [])
          : rows.map((obj) => obj[c.field]);
        console.log("fields", fields);
        const uniqValues = this.getUniqs(c.field, fields);
        let payload = [
          {
            value: "Unassigned",
            visible: false,
          },
        ];

        console.log("uniqValues", uniqValues);
        uniqValues.forEach((u) => {
          if (u) {
            console.log("u", u);
            payload.push({
              value: u,
              visible: false,
            });
          }
        });

        console.log(":final araa", payload);

        this.columnDefs[indexOfO].filterValueArray = payload;
      });
    },
    formatJob(arr, skipUniqs, single) {
      return new Promise((res, rej) => {
        let a = [];
        for (let i = 0; i < arr.length; i++) {
          let _formatAddress = arr[i].formattedAddress || "";

          let _date = "";
          if (arr[i].date.seconds) _date = new Date(arr[i].date.seconds * 1000);
          else _date = new Date(arr[i].date);

          let payload = {
            visible: true,
            projectId: arr[i].projectId,
            rejected: arr[i].rejected,
            projectName: arr[i].projectName,
            date: this.$moment(_date).format("DD MMM"),
            time: new Date(_date),
            client: arr[i].client,
            customerId: arr[i].customerId,
            customer: arr[i].company,
            region: arr[i].region,
            company: arr[i].company,
            fullAddress: _formatAddress || "",
            address: arr[i].address || "",
            geopoint: arr[i].geopoint || "",
            geohash: arr[i].geohash || "",
            mixCode: arr[i].mixCode,
            meters: arr[i].meters,
            duration: arr[i].duration,
            accepted: arr[i].accepted,
            id: arr[i].id,
            status: arr[i].status,
            requirements: arr[i].requirements,
            pourLocation: arr[i].pourLocation,
            notesForTester: arr[i].notesForTester,
            poNumber: arr[i].poNumber,
            tester: "",
            email: arr[i].email,
            phone: arr[i].phone,
            contact: arr[i].contact,
            secondaryContact: arr[i].secondaryContact || "",
            secondaryPhone: arr[i].secondaryPhone || "",
            secondaryEmail: arr[i].secondaryEmail || "",
            jobContact: arr[i].jobContact || "",
            jobPhone: arr[i].jobPhone || "",
            jobEmail: arr[i].jobEmail || "",
            testerPhone: "",
            testerEmail: "",
            testerNotes: arr[i].testerNotes,
            notes: arr[i].notes,
            published: arr[i].published,
            clientSmsStatus: arr[i].clientSmsStatus || null,
            formattedAddress: arr[i].formattedAddress || "",
            testerStartTime: arr[i].testerStartTime || null,
            enRoute: arr[i].enRoute || false,
            teamLeaders: arr[i].teamLeaders || [],
            teamLeaderIds: arr[i].teamLeaderIds || [],
            teamLeader: (arr[i].teamLeaders || []).map(tl => tl.name).join(', ') || '',
            contractor: arr[i].contractor || null,
            ...this.customFieldRowValues(arr[i]),
          };

          // Add tester if it exists
          if (arr[i].contractor) {
            payload.tester = arr[i].contractor.name;
            payload.testerId = arr[i].contractor.id || null;
            payload.testerPhone = arr[i].contractor.phone;
            payload.testerEmail = arr[i].contractor.email;
          }
          a.push(payload);
        }

        if (!single) {
          this.rowData = a;
          res();
        } else res(a[0]);
      });
    },
    async getTesters() {
      const usersCollection = collection(db, "users");

      const q = query(usersCollection, where("active", "==", true));

      await getDocs(q)
        .then((querySnapshot) => {
          if (querySnapshot.empty) {
            console.log("No documents found matching the criteria");
            return;
          }

          querySnapshot.forEach((doc) => {
            this.testers.push({ ...doc.data(), id: doc.id });
          });
        })
        .catch((error) => {
          console.log("Error getting documents:", error);
        });
    },
    async updateTeamLeaderJobStatus(job) {
      try {
        await setFire({ collection: 'jobs', doc: job.id, body: { status: job.status } });
      } catch (e) {
        console.error('Error updating job status:', e);
      }
    },
    async assignTeamMemberToJob(job, memberId) {
      try {
        let contractor = null;
        if (memberId) {
          const member = this.teamMembers.find(m => m.id === memberId);
          if (member) {
            contractor = { id: member.id, name: member.name, phone: member.phone || '', email: member.email || '' };
          }
        }
        await setFire({ collection: 'jobs', doc: job.id, body: { contractor } });
      } catch (e) {
        console.error('Error assigning tester:', e);
      }
    },
    async saveTeamLeaderJobNotes(job) {
      try {
        await setFire({ collection: 'jobs', doc: job.id, body: { notes: job.notes || '' } });
      } catch (e) {
        console.error('Error saving notes:', e);
      }
    },
    startInviteListener() {
      if (this.inviteUnsubscribe) { this.inviteUnsubscribe(); this.inviteUnsubscribe = null; }
      const q = query(
        collection(db, 'teamLeaderInvites'),
        where('teamLeaderId', '==', this.currentUser.id),
        where('status', '==', 'pending')
      );
      this.inviteUnsubscribe = onSnapshot(q, (snapshot) => {
        const invites = [];
        snapshot.forEach(d => invites.push({ ...d.data(), _id: d.id }));
        this.pendingInvites = invites;
        if (!this.currentInvite && invites.length > 0) {
          this.currentInvite = invites[0];
        }
      });
    },
    async acknowledgeInvite() {
      if (!this.currentInvite) return;
      try {
        await setFire({ collection: 'teamLeaderInvites', doc: this.currentInvite._id, body: { status: 'acknowledged' } });
      } catch (e) { console.error(e); }
      this.advanceInviteQueue();
    },
    async rejectInvite() {
      if (!this.currentInvite) return;
      const invite = this.currentInvite;
      try {
        // Remove this team leader from the job
        const jobDoc = await getFire({ collection: 'jobs', doc: invite.jobId });
        if (jobDoc) {
          const tls = (jobDoc.teamLeaders || []).filter(tl => tl.id !== this.currentUser.id);
          const tlIds = (jobDoc.teamLeaderIds || []).filter(id => id !== this.currentUser.id);
          await setFire({ collection: 'jobs', doc: invite.jobId, body: { teamLeaders: tls, teamLeaderIds: tlIds } });
        }
        await setFire({ collection: 'teamLeaderInvites', doc: invite._id, body: { status: 'rejected' } });
      } catch (e) { console.error(e); }
      this.advanceInviteQueue();
    },
    advanceInviteQueue() {
      const remaining = this.pendingInvites.filter(i => i._id !== (this.currentInvite && this.currentInvite._id));
      this.currentInvite = remaining.length > 0 ? remaining[0] : null;
    },
    formatInviteDate(d) {
      if (!d) return '';
      if (d.seconds) return this.$moment(new Date(d.seconds * 1000)).format('ddd DD MMM, h:mmA');
      return this.$moment(new Date(d)).format('ddd DD MMM, h:mmA');
    },
    openTeamLeaderPicker(event, job) {
      this.teamLeaderPickerPos = this.anchorPopover(event.currentTarget.getBoundingClientRect(), 256);
      this.teamLeaderPickerJob = job;
    },
    async toggleTeamLeaderOnJob(job, tl) {
      const tlIds = job.teamLeaderIds ? [...job.teamLeaderIds] : [];
      let tls = job.teamLeaders ? [...job.teamLeaders] : [];
      const at = tlIds.indexOf(tl.id);
      if (at === -1) {
        tlIds.push(tl.id);
        tls.push({ id: tl.id, name: tl.name });
      } else {
        tlIds.splice(at, 1);
        tls = tls.filter(t => t.id !== tl.id);
      }
      try {
        await setFire({ collection: 'jobs', doc: job.id, body: { teamLeaders: tls, teamLeaderIds: tlIds } });
        this.$set(job, 'teamLeaders', tls);
        this.$set(job, 'teamLeaderIds', tlIds);
        this.$set(job, 'teamLeader', tls.map(t => t.name).join(', '));
        if (at === -1) await createTeamLeaderInvite(tl.id, job.id, job);
      } catch (e) {
        console.error('Error updating team leaders:', e);
      }
    },
    async inviteTeamLeaderToJob(tl) {
      const job = this.inviteModalJob;
      if (!job) return;
      const tls = job.teamLeaders ? [...job.teamLeaders] : [];
      const tlIds = job.teamLeaderIds ? [...job.teamLeaderIds] : [];
      tls.push({ id: tl.id, name: tl.name });
      tlIds.push(tl.id);
      try {
        await setFire({ collection: 'jobs', doc: job.id, body: { teamLeaders: tls, teamLeaderIds: tlIds } });
        job.teamLeaders = tls;
        job.teamLeaderIds = tlIds;
        job.teamLeader = tls.map(t => t.name).join(', ');
        // Create invite notification
        await createTeamLeaderInvite(tl.id, job.id, job);
      } catch (e) {
        console.error('Error inviting team leader:', e);
      }
      this.inviteModalJob = null;
    },
    scrollToJob(jobId) {
      if (!jobId) return;
      // Clear any previous highlight
      if (this._scrollHighlightTimer) clearTimeout(this._scrollHighlightTimer);
      const prev = this.$refs.scrollBody && this.$refs.scrollBody.querySelector('tr.ring-2');
      if (prev) prev.classList.remove('ring-2', 'ring-indigo-400');
      this.$nextTick(() => {
        const container = this.$refs.scrollBody;
        if (!container) return;
        const targetTr = container.querySelector(`tr[data-job-id="${jobId}"]`);
        if (targetTr) {
          targetTr.scrollIntoView({ behavior: 'smooth', block: 'center' });
          targetTr.classList.add('ring-2', 'ring-indigo-400');
          this._scrollHighlightTimer = setTimeout(() => {
            targetTr.classList.remove('ring-2', 'ring-indigo-400');
          }, 3000);
        }
      });
    },
  },
  beforeDestroy() {
    if (unsubscribe) {
      unsubscribe();
    }
    if (notifUnsubscribe) {
      notifUnsubscribe();
    }
    bus.$off('openBulkSms');
    bus.$off('openStartTimes');
    bus.$off('openMapView');
    this.$root.$off('scroll-to-job', this.scrollToJob);
    this.destroyScheduleMap();
    if (this.nowTimeTimer) clearInterval(this.nowTimeTimer);
    if (this.inviteUnsubscribe) { this.inviteUnsubscribe(); }
  },
  async created() {
    this.scheduleLoading = true;
    this.baseColumnFields = this.columnDefs.map((c) => c.field);
    // Fetch fresh user doc from Firestore to get latest column config
    try {
      const authUser = await this.getCurrentUser();
      if (authUser) {
        const freshUser = await getFire({ collection: 'users', doc: authUser.uid });
        if (freshUser) {
          this.$store.commit('updateState', { state: 'currentUser', body: freshUser });
        }
      }
    } catch (e) {
      console.log('Could not refresh user data:', e);
    }
    this.customFieldDefs = await loadDefinitions();
    if (this.customFieldsEnabled && this.currentUser && this.currentUser.scheduleSelectedService) {
      const saved = this.currentUser.scheduleSelectedService;
      // Fall back to All Services if the saved service was since deactivated.
      if (saved === "all" || this.customServices.some((s) => s.key === saved)) {
        this.selectedService = saved;
      }
    }
    // Splices in any custom columns, then runs initColumnConfig().
    this.rebuildColumnDefs();
    this.getItems();
    this.getTesters();
    this.startNotificationsListener();

    // Load team members and all team leaders for team leader
    if (this.isTeamLeader) {
      try {
        const [members, leaders] = await Promise.all([
          getTeamMembers(this.currentUser.id),
          getTeamLeaders(),
        ]);
        this.teamMembers = members;
        this.allTeamLeaders = leaders;
      } catch (e) {
        console.error('Error loading team data:', e);
      }
      // Listen for pending invite notifications
      this.startInviteListener();
    } else if (this.currentUser && this.currentUser.role === 'admin') {
      // Admins assign team leaders from the schedule table
      try {
        this.allTeamLeaders = await getTeamLeaders();
      } catch (e) {
        console.error('Error loading team leaders:', e);
      }
    }

    // Load today's timesheet for contractor start time banner
    if (this.currentUser && this.currentUser.id && this.currentUser.role === 'contractor') {
      try {
        const ts = await getTodaysTimesheet(this.currentUser.id);
        if (ts && ts.scheduledStart) {
          this.timesheetStartTime = ts.startOverride || ts.scheduledStart;
        }
      } catch (e) {
        console.error('Error loading timesheet start time:', e);
      }
    }

    this.scheduleLoading = false;
    bus.$on('openBulkSms', () => { this.openBulkSmsModal(); });
    bus.$on('openStartTimes', () => { this.openStartTimesModal(); });
    bus.$on('openMapView', () => { this.openMapModal(); });
    this.$root.$on('scroll-to-job', this.scrollToJob);
    this.nowTimeTimer = setInterval(() => { this.nowTime = new Date(); }, 60000);
  },
  components: {
    Datetime,
    flatPickr,
  },
};
</script>

<style scoped>
.flex-shrink-0 {
  flex-shrink: 0;
}
.animate-bounce-in {
  animation: bounceIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}
@keyframes bounceIn {
  0% { transform: scale(0.3); opacity: 0; }
  50% { transform: scale(1.05); }
  70% { transform: scale(0.95); }
  100% { transform: scale(1); opacity: 1; }
}
.now-time-row {
  height: 4px;
  padding: 0;
  margin: 0;
  background: none;
  border: none;
  pointer-events: none;
}
.now-time-cell {
  padding: 0;
  margin: 0;
  height: 4px;
  position: relative;
  border: none;
  pointer-events: none;
}
.now-time-line {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  height: 3px;
  background: rgba(239, 68, 68, 0.75);
  z-index: 3;
  pointer-events: none;
}
.now-time-line::before {
  content: '';
  position: absolute;
  left: -5px;
  top: -5px;
  width: 12px;
  height: 12px;
  background: rgba(239, 68, 68, 0.75);
  border-radius: 50%;
  pointer-events: none;
}
</style>
